"use client"

import './pdp-rail-card.css'
import { RecentViewRecorder, RecentReturn } from '@/components/homepage-2026/recent-return'
import { useState, useEffect, useMemo, useRef, useCallback } from "react"
import { observeHeroBoundary } from "@/lib/hero-boundary"
import { TrustStrip } from "@/components/trust/trust-strip"
import { GooglePreferredButton } from "@/components/chrome/google-preferred-button"
import { SafeImage } from "@/components/ui/safe-image"
import { NoImageFallback } from "@/components/ui/no-image-fallback"
import { BrandedImage } from "@/components/branded-image"
import { getProxiedStoragePath, isRenderableImageUrl } from "@/lib/proxied-storage-path"
import Link from "next/link"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Star, ChevronLeft, ChevronRight, Zap } from "lucide-react"
import { ArrowUpRightIcon, ChevronLeftIcon, ChevronRightIcon } from "@/components/brand-kit/icon-sprite"
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel"
import { formatCollectionName, formatProductTitle } from "@/lib/breadcrumb-utils"
import { productBreadcrumbs } from "@/lib/breadcrumbs-product"
import type { BreadcrumbTrail } from "@/lib/breadcrumbs"
import { Breadcrumb } from "@/components/brand-kit/breadcrumb"
import {
  getRelatedProducts,
  getFeaturedProductsWithFallback,
  getClosestAlternatives,
  type RecommendedProduct
} from "@/lib/recommendations"
import type { PdpRailProduct, PdpRails } from "@/lib/products/pdp-rail-card"
import { formatProductType } from "@/lib/collections"
import { getCategoryDisplayNamePlural, resolveDisplayModel } from "@/lib/url-format"
import { buildProductDisplayTitle, buildNonApplianceDisplayTitle, resolveTitleModel } from "@/lib/products/product-display-title"
import { displayBrandName, isKnownBrand } from "@/lib/brand-casing"
import { FinBadge } from "@/components/product/FinBadge"
import { ProvenanceLine } from "@/components/product/ProvenanceLine"
import { FpnBadge } from "@/components/product/FpnBadge"
import { FfnBadge } from "@/components/product/FfnBadge"
import { FilterCertificationTable } from "@/components/filters/certification-table"
import { FilterPerformanceTable } from "@/components/filters/performance-table"
import { ListingClaimsCard } from "@/components/filters/listing-claims"
import { FilterPdpToolLinks } from "@/components/filters/pdp-tool-links"
import { FilterBrandTile } from "@/components/filters/filter-brand-tile"
import { isPartType, productPagePath, familyForType, familyRoot } from "@/lib/parts/fpn"
import { SignedOfferLink } from "@/components/product/signed-offer-link"
import { UpdatedStamp } from "@/components/product/UpdatedStamp"
import { CustomerReviews } from "@/components/product/CustomerReviews"
import { RecallBanner } from "@/components/product/RecallBanner"
import type { FinRecallRow } from "@/lib/recalls/types"
import { RetailerOfferCta, LearnAboutRecallLink } from "@/components/product/retailer-offer-cta"
import { hasActiveRecall as deriveHasActiveRecall } from "@/lib/product/recall-cta"
import { VideoReviews, type FinVideo } from "@/components/product/VideoReviews"
import { ProductDocuments } from "@/components/product/ProductDocuments"
import { FromTheManual } from "@/components/product/FromTheManual"
import { ManualDiagrams } from "@/components/product/ManualDiagrams"
import { InternetVerdict, hasVerdictEvidence, type WtisRow } from "@/components/product/InternetVerdict"
import { DealVerdict } from "@/components/deal-verdict/DealVerdict"
import { dealVerdictModuleEnabled } from "@/components/deal-verdict/flag"
import type { DealVerdict as DealVerdictData } from "@/components/deal-verdict/compute"
import { PriceIntelligence } from "@/components/price-intelligence/PriceIntelligence"
import { derivePriceIntelligence, priceIntelStatsFor, type SparkPoint } from "@/components/price-intelligence/derive"
import { selectBuyBox } from "@/lib/offer-selection"
import { canUseServerBuyBox } from '@/lib/products/buy-box-candidates'
import { isOfferPriceUnavailable } from '@/lib/products/display-offer-selection'
import { hubCardDescription } from "@/lib/hub-card-description"
import { ProductGridCard } from "@/components/brand-kit/product-grid-card"
import { NormalizedProductImage } from "@/components/product/NormalizedProductImage"
import { ProductImageShareSheet } from "@/components/product/ProductImageShareSheet"
import type { DisplayPdf } from "@/lib/render-policy/display-pdfs"
import { pickCompareSpecKeys, compareRowLabel } from "@/lib/render-policy/display-comparables"
import { formatSpecValue, formatPrice, formatCents } from "@/lib/format-utils"
import { formatSaleFrequencyLine, type SaleFrequency } from "@/lib/deals/sale-frequency"
import { dealCtaLabel, hasRenderedPrice } from "@/lib/deal-cta"
import { productCardQualifier } from "@/lib/card-link-names"
import {
  ReviewTopicFilter,
  shouldRenderReviewTopicFilter,
  selectedReviewIdSet,
  type ReviewTopicEntry,
} from "@/components/product/ReviewTopicFilter"
import {
  ProductHeroBadges,
  BadgeList,
  badgesFromTypes,
  badgesFromRecommended,
  determineBadges,
  DealBadge,
  SavingsPercentageBadge,
  DealCountdown,
  LowStockWarning,
  BuyBoxWinnerBadge,
  PricePerUnit
} from "@/components/badges"
import { ComparisonBar } from "@/components/comparison-bar"
import { ProductTracking } from "@/components/product-tracking"
import { analyzeProduct } from "@/lib/product-analytics"
import { CurrentOfferDiscount } from "@/components/price-intelligence/CurrentOfferDiscount"
import { priceEvidenceBadges } from "@/components/price-intelligence/price-evidence-badges"
import { ProductFAQ } from "@/components/blocks/product-faq"
import { SpecFAQ } from "@/components/blocks/product-faq/spec-faq"
import { SPEC_FAQ_MIN_RENDER } from "@/lib/faq/spec-faq-builder"
import { TechnicalSpecs } from "@/components/blocks/technical-specs"
import { ProductShareButtons } from "@/components/blocks/product-share-buttons"
import { NotFoundState } from "@/components/blocks/not-found-state"
import dynamic from "next/dynamic"
import { describeFitmentCoverage } from "@/lib/filters/fitment-caps"
import { BrandLogoGrid, BrandTileContent } from "@/components/brand-kit/brand-logo-grid"
import { canonicalBrandKey } from "@/lib/brands/grid-order"
import { StarRating } from "@/components/brand-kit/star-rating"
import { ToolLinkCard } from "@/components/brand-kit/tool-link-card"
import { fetchWithDelay } from "@/lib/api-fetch"
import { normalizeDimensions } from "@/lib/dimension-utils"
import { moduleFlag } from "@/lib/pip/module-flags"
import { isProductReview } from "@/lib/render-policy/review-topicality"
import { useStickyColumn } from "@/hooks/use-sticky-column"
import { PdpStickyBar, type PdpStickyBarAnchor } from "@/components/product/PdpStickyBar"
import { MobileStickyBuyBar } from "@/components/product/MobileStickyBuyBar"
// FRIDGE-874 — the product page had NO Save control and no compare tick of its
// own; the only way onto the compare list from here was the legacy tray, which
// nothing on the page could populate. These two write the shared store.
import { SaveCompareRow, SaveHeartButton } from "@/components/chrome/save-compare-controls"
import { buildSavedItem } from "@/lib/saved-compare-item"
import { PriceWatchForm } from "@/components/price-watch/price-watch-form"
import { RecallAlertForm } from "@/components/price-watch/recall-alert-form"
import { AffiliateDisclaimer } from "@/components/affiliate-disclaimer"

// "Compatible Water Filters" renders only on appliance pages that have fitted
// filters, deep in "Complete the Setup". A static import put the card, its
// brand tile and fitment helper into the First Load JS of every page that
// renders this client, filter detail pages included, where it never shows
// (measured 09/19/2026 when the bundle budget went red on /filters/[slug]).
// Server rendering stays ON (the default): the section's filter links and
// FFNs are in the HTML crawlers read; only its browser code is split out.
const CompatibleFilterSection = dynamic(() =>
  import("@/components/blocks/compatible-filter").then((m) => m.CompatibleFilterSection)
)

// Desktop sticky-column clearance is `lg:top-28` (112px) on the pinned column
// below — sized to clear the sticky site header (h-[86px] + 6px accent border
// = ~92px) plus ~20px breathing room.

// Enrichment/preview sections are gated PER MODULE (PIP lean rebuild WS0.6):
// NEXT_PUBLIC_MODULE_{VIDEOS,XRETAIL,REVIEWS,PDFS,FAQ,COMPARE} — '1' force-on,
// '0' force-off, unset → the module's pre-split behavior (the legacy combined
// NEXT_PUBLIC_SHOW_ENRICHMENT + NODE_ENV=development gate for the five
// preview sections; always-on for AutoFAQ, which was never preview-gated).
// So nothing changes until an env var is set — see lib/pip/module-flags.ts,
// including the NEXT_PUBLIC_* build-time inlining caveat (flag changes need a
// rebuild). Each section keeps its own Real-or-Hidden data gate too.
// (2026-06-20, split 2026-07-05)
// Go-live (owner decision 2026-07-08): default-ON in prod so the live site
// matches dev — the modules were dev-only while their data was validated
// (moderation backfill, video classification, comparables). `true` default
// still honors a `NEXT_PUBLIC_MODULE_<X>=0` kill switch, and each section
// keeps its own Real-or-Hidden data gate, so a product without the data (no
// reviews, no verdict evidence, no videos, no comparables) still self-hides.
const SHOW_MODULE_VIDEOS = moduleFlag('VIDEOS', true)
// XRETAIL removed (conversion spec P0) — the cross-retailer widget is gone.
const SHOW_MODULE_REVIEWS = moduleFlag('REVIEWS', true)
const SHOW_MODULE_VERDICT = moduleFlag('VERDICT', true)
// PDFS default-ON (owner note 07-06): the Manuals & Guides section replaces
// the removed Parts & Manuals card. page.tsx resolves strict (hosted/verified
// only) outside dev, so prod can never show unverified discovered docs.
const SHOW_MODULE_PDFS = moduleFlag('PDFS', true)
// AutoFAQ pre-split behavior: ALWAYS rendered (own data gate only) → unset
// falls back to true, '0' now gives the owner a kill switch.
const SHOW_MODULE_FAQ = moduleFlag('FAQ', true)
const SHOW_MODULE_COMPARE = moduleFlag('COMPARE', true)
// PRICE_WATCH relaunched default-ON (plan item 181, owner-directed
// consumer-email mandate). The 2026-07-11 pause predated the live email
// stack: the double-opt-in CONFIRM email now sends transactionally via
// Resend (lib/email-sender.ts), so the capture surface is honest again —
// a signup produces a real confirmation, and nothing further sends until
// the marketing gate (ALERTS_EMAIL_ENABLED + CAN_SPAM_ADDRESS) opens.
// NEXT_PUBLIC_MODULE_PRICE_WATCH=0 remains the kill switch.
const SHOW_MODULE_PRICE_WATCH = moduleFlag('PRICE_WATCH', true)
// RECALL_ALERT stays paused (owner hotfix 2026-07-11): unset = dev-preview
// only; NEXT_PUBLIC_MODULE_RECALL_ALERT=1 + redeploy relaunches.
const SHOW_MODULE_RECALL_ALERT = moduleFlag('RECALL_ALERT')

// ─────────────────────────────────────────────────────────────────────────────
// Inline helpers — full-bleed product carousel + accordions (matches test page)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Grounded verdict facts — extracted from the former inline Expert Verdict
 * IIFE (2026-07-08 reorder). Assembles ONLY real, checkable product facts
 * (capacity, dimensions, ENERGY STAR, live price). The two consumers share
 * this one builder: when the InternetVerdict module renders, these fold into
 * its "Fit & facts" list; otherwise the legacy Expert Verdict card renders
 * them (which still needs >= 2 facts, or it self-hides — Real-or-Hidden 3).
 *
 * `priceDisplay` is the buybox winner's already-formatted price string
 * (bestFreshOffer.priceDisplay) — the SAME value the offers card and the
 * verdict CTA show. We must NOT re-derive price from product.price here:
 * that field is offers[0].price (the raw first offer, before the freshness
 * sort), so it can disagree with the presented buybox price. When there's no
 * fresh offer, the price fact is simply omitted (Real-or-Hidden — don't print
 * a stale/unknown number).
 */
function buildVerdictFacts(product: any, analysis: any, priceDisplay?: string | null): string[] {
  const facts: string[] = []

  if (product.capacity_cuft) {
    const cap = Number(product.capacity_cuft)
    const sizing =
      cap >= 25 ? 'enough room for a large household' :
      cap >= 18 ? 'enough room for the average household weekly grocery run' :
      cap >= 10 ? 'a practical mid-size footprint' :
      'a compact footprint that fits tight kitchens, dorms, and offices'
    facts.push(`${cap} cu. ft. of usable storage — ${sizing}.`)
  }

  if (product.width_in && product.height_in && product.depth_in) {
    facts.push(
      `Measures ${product.width_in}″ W × ${product.height_in}″ H × ${product.depth_in}″ D, so confirm your alcove and door swing before ordering.`
    )
  }

  if (analysis?.features?.isEnergyStarCertified) {
    facts.push('ENERGY STAR® certified, which translates to lower utility bills over the unit\'s lifetime.')
  }

  if (typeof priceDisplay === 'string' && priceDisplay.trim().length > 0) {
    facts.push(`Currently listed at ${priceDisplay} based on the live offers above.`)
  }

  return facts
}

export function FullBleedProductCarousel({
  products,
  label,
  title,
  subtitle,
  bgClass = "bg-gray-50",
  slot,
  minItems = 1,
  maxItems,
}: {
  /** FRIDGE-1524: the card fields (lib/products/pdp-rail-card.ts); a browser-read row has them too. */
  products: PdpRailProduct[]
  label: string
  title: string
  subtitle: string
  bgClass?: string
  /** Featured-rail slot retained in data attributes for click attribution.
   *  The same product keeps one canonical destination across every rail. */
  slot?: 'rail-a' | 'rail-b' | 'rail-c'
  /** Below this many items the rail self-hides — a 2-card rail reads as a
   *  dying store (Real-or-Hidden Rule 3 extension). Personal rails pass 1. */
  minItems?: number
  /** Hard cap on cards; more rails than this dilute every rail's CTR. */
  maxItems?: number
}) {
  const scrollRef = useRef<HTMLDivElement>(null)
  if (!products || products.length < minItems) return null

  const shown = typeof maxItems === 'number' ? products.slice(0, maxItems) : products

  const scroll = (direction: "left" | "right") => {
    if (!scrollRef.current) return
    scrollRef.current.scrollBy({ left: direction === "left" ? -300 : 300, behavior: "smooth" })
  }

  const isWhite = bgClass === "bg-white"

  return (
    <section className={`${bgClass} rounded-xl py-8 px-5 md:px-8 mt-10 ${isWhite ? "border border-gray-200" : ""}`}>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4 mb-6">
        <div>
          <span className="text-[#ff9951] font-bold tracking-widest text-xs mb-2 block uppercase">{label}</span>
          <h2 className="text-2xl md:text-3xl font-black tracking-tighter leading-tight text-gray-900">{title}</h2>
          <p className="text-gray-500 max-w-md text-sm leading-relaxed">{subtitle}</p>
        </div>
        <div className="flex gap-3">
          {/* FRIDGE-1524: the rails are in the server HTML now, so their icons are
              the sprite's (components/brand-kit/icon-sprite.tsx), as on the
              shared carousels: one definition per document, not a path per card. */}
          <button onClick={() => scroll("left")} aria-label="Scroll left" className="prc-nav">
            <ChevronLeftIcon className="w-5 h-5 text-[#ff9951]" />
          </button>
          <button onClick={() => scroll("right")} aria-label="Scroll right" className="prc-nav">
            <ChevronRightIcon className="w-5 h-5 text-[#ff9951]" />
          </button>
        </div>
      </div>

      <div ref={scrollRef} className="flex overflow-x-auto gap-6 snap-x snap-mandatory pb-4" style={{ scrollbarWidth: "none", msOverflowStyle: "none", WebkitOverflowScrolling: "touch" }}>
        {shown.map((p) => {
          // FRIDGE-333: a rail card whose row is a part links to /parts/<slug>
          // (its canonical_url column still reads /products/<slug> — that URL
          // 301s; the card should not need the hop).
          const railSlug = (p.canonical_url || '').replace(/^\/products\//, '')
          const railPath = railSlug ? productPagePath({ type: p.type, slug: railSlug }) : null
          const href = railPath || p.canonical_url || `/products/${p.id}`
          const identifier = productCardQualifier(href) || resolveDisplayModel(p.model, null)
          const img = p.images?.[0] || "/placeholder.svg"
          const hasPrice = hasRenderedPrice(p.price)
          const priceStr = hasPrice ? formatPrice(p.price!) : ''
          const origStr = typeof p.originalPrice === 'number' && p.originalPrice > (p.price || 0)
            ? formatPrice(p.originalPrice)
            : ''
          // The whole card is the link, so its name is the card's own text: brand,
          // title, the visible FIN, price and the CTA (unique per card by title and
          // FIN). A "Get Deal: FIN" aria-label on top of that text failed axe-core
          // 4.14.0's label-content-name-mismatch (WCAG 2.5.3) on every product page.
          // FRIDGE-1663: each card part carries one short class (prc-*), whose
          // utilities ./pdp-rail-card.css writes once, instead of repeating
          // about 1 KB of utility classes per card in the server HTML.
          return (
            <Link key={p.id} href={href} data-ph-capture-attribute-recommendation-source={slot ? `pdp-${slot}` : undefined} className="prc group">
              <div className="prc-box">
                {/* Canonical badges (spec §6 cut-over) — same engine/array as
                    every other surface, via the dollars→cents recommended-shape
                    adapter. Real-or-Hidden: BadgeList renders nothing when the
                    product earns no badge. */}
                <BadgeList
                  badges={badgesFromRecommended(p)}
                  variant="carousel"
                  placement="inline"
                  className="absolute top-3 left-3 z-10"
                />
                <div className="prc-img product-image-canvas">
                  <SafeImage
                    src={img}
                    alt={`${p.title} | Fridge.com`}
                    width={260}
                    height={260}
                    className="prc-pic"
                    loading="lazy"
                  />
                </div>
                <div className="prc-body">
                  <div className="mb-2">
                    {/* FRIDGE-1501: the brand name is text. It sits inside the card's product link,
                        and the scripted click handler there was a link crawlers could not follow
                        and keyboards could not reach, which sent a tap on the name to the brand page
                        instead of the product. The brand page stays one tap away on the PDP itself. */}
                    {p.brand && (
                      <span className="prc-brand">{displayBrandName(p.brand)}</span>
                    )}
                    <h3 className="prc-title">{formatProductTitle(p.title)}</h3>
                    {identifier && <p className="prc-id">{identifier}</p>}
                  </div>
                  {typeof p.rating === 'number' && p.rating > 0 && (
                    <div className="mb-2 w-fit"><StarRating rating={p.rating} /></div>
                  )}
                  <div className="mt-auto">
                    {hasPrice ? (
                      <div className="prc-row">
                        <div className="prc-price">{priceStr}</div>
                        {origStr && <div className="text-xs text-gray-600 line-through">{origStr}</div>}
                      </div>
                    ) : null}
                    <span className="prc-cta">
                      {dealCtaLabel(hasPrice)}
                      <ArrowUpRightIcon className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          )
        })}
      </div>
    </section>
  )
}

// Props interface for ISR - server passes initial data
interface ProductClientProps {
  initialData: any | null
  slug: string
}

export default function ProductClient({ initialData, slug }: ProductClientProps) {
  // ?preview=1 is read from window.location, NOT useSearchParams(). PDPs are
  // served from a static/ISR prerender (page.tsx: revalidate = 300), and a
  // useSearchParams() call anywhere in this component makes that prerender
  // bail the ENTIRE subtree out of server rendering
  // (BAILOUT_TO_CLIENT_SIDE_RENDERING): the emitted HTML carried only the
  // Suspense fallback — no offers card, no Price Watch, no PDP body — until
  // the browser ran the client bundle. Dev-server renders are always dynamic,
  // which is why the miss never reproduced in development. Reading
  // window.location keeps the prerender fully server-rendered; the server
  // value (false) is exact for the prerender, and both consumers of `preview`
  // act only on the client: the notFound initial state matters only on the
  // client-only fallback path (initialData === null, which page.tsx never
  // server-renders — it 404s first), and the fetch URL is built inside a
  // browser-only effect.
  const preview =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('preview') === '1'

  const [selectedImageIndex, setSelectedImageIndex] = useState(0)

  // FRIDGE-1524: the three "more products" rows as the server read them
  // (page.tsx, lib/products/pdp-rails.ts). A row the server answered is in the
  // server HTML and is not read again in the browser; a row whose server read
  // failed (null) and the client-only fallback path read in the browser as
  // they did before.
  const serverRails: PdpRails | null = initialData?.rails ?? null
  const serverClosest = serverRails?.closest ?? null
  const serverTopRated = serverRails?.topRated ?? null
  const serverBrands = serverRails?.brands ?? null
  // FRIDGE-1623: the server sends the grid's first tiles and how many there are.
  const serverBrandsTotal = serverRails?.brandsTotal ?? null

  // Initialize with server-provided data (for ISR) or null (for client-only fallback)
  const [productData, setProductData] = useState<any>(initialData)
  const [brands, setBrands] = useState<any[]>(serverBrands ?? [])
  // FRIDGE-1623: true once `brands` holds every partner brand, not just the first tiles.
  const [allBrandsLoaded, setAllBrandsLoaded] = useState(!serverBrands || serverBrandsTotal === null || serverBrandsTotal <= serverBrands.length)
  const brandsLoadStarted = useRef(false)
  const [loading, setLoading] = useState(!initialData) // Only loading if no initial data
  const [notFound, setNotFound] = useState(initialData === null && !preview)
  const [error, setError] = useState<string | null>(null)

  // Hub page links passed from server for visible cross-linking
  const relatedHubs = productData?.relatedHubs || []

  // Related products (used for analytics/dedup)
  const [relatedProducts, setRelatedProducts] = useState<RecommendedProduct[]>([])

  // Featured rails — read on the server (FRIDGE-1524), or via useEffect below
  // when the server read failed. The old "Cool Right Now" /
  // "Best Value" / "Bestsellers" rails were CUT (2026-07-08 reorder): trending
  // framing belongs on discovery pages, and a site-wide bestsellers mix
  // (cross-category) diluted a PDP. What survives: Slot B "Closest
  // alternatives" (scoped), Slot C "Top-rated" (reuses featured), and the
  // personal Recently-Viewed rail.
  const [closestAltProducts, setClosestAltProducts] = useState<PdpRailProduct[]>(serverClosest ?? [])
  const [topRatedProducts, setTopRatedProducts] = useState<PdpRailProduct[]>(serverTopRated ?? [])

  const [compareTab, setCompareTab] = useState<'this-item' | 'recommendations'>('this-item')

  // Phase 8 step 8: <ReviewTopicFilter> selection state. null = "All" (no
  // filter active). Lifted to this parent so the same array of reviews can
  // be passed to both <ReviewTopicFilter> (which renders chips) and
  // <CustomerReviews> (which renders the constrained list).
  const [selectedReviewTopic, setSelectedReviewTopic] = useState<string | null>(null)
  // FRIDGE-179 / crawler-eye 08/22: the server flight ships only the initial
  // review window (SERIALIZED_REVIEWS_MAX in lib/render-policy/display-reviews).
  // The FIRST interaction that needs the corpus — search, star filter,
  // photos-only, sort, topic select, show-more — fetches the full eligible set
  // once from /api/reviews/[fin] and swaps it in. Failure keeps the initial
  // window and allows a retry on the next interaction; the fetch never blocks
  // the interaction itself.
  const [fullReviews, setFullReviews] = useState<any[] | null>(null)
  const [reviewCorpusLoading, setReviewCorpusLoading] = useState(false)
  const reviewCorpusInflight = useRef(false)
  // useCallback so the React Compiler can preserve the surrounding manual
  // memoization (a bare closure here made it bail on this file's useMemos —
  // caught by the eslint ratchet, react-hooks/preserve-manual-memoization).
  const ensureReviewCorpus = useCallback(() => {
    const fin = (productData.product as any)?.reviewsFin as string | null
    const eligible = (productData.product as any)?.reviewsMeta?.eligibleCount ?? 0
    const loaded = fullReviews?.length ?? ((productData.product as any)?.customerReviews?.length ?? 0)
    if (!fin || fullReviews || reviewCorpusInflight.current || eligible <= loaded) return
    reviewCorpusInflight.current = true
    setReviewCorpusLoading(true)
    fetch(`/api/reviews/${encodeURIComponent(fin)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((j) => {
        if (Array.isArray(j?.reviews) && j.reviews.length > 0) setFullReviews(j.reviews)
      })
      .catch(() => {
        // Retryable: the next qualifying interaction attempts again.
        reviewCorpusInflight.current = false
      })
      .finally(() => setReviewCorpusLoading(false))
  }, [fullReviews, productData])
  // P1 buybox: secondary offers collapse behind "Compare N more offers".
  const [showAllOffers, setShowAllOffers] = useState(false)
  // Sidebar description clamps by default so the sidebar's baseline height
  // fits the viewport in the common case — which is what lets the chooser pin
  // it (it only sticks a column that fits). "Read more" expands it in place
  // without re-flowing the sticky column (see hooks/use-sticky-column.ts).
  const [descExpanded, setDescExpanded] = useState(false)
  // Sticky CTA bars (mobile + desktop) appear once the hero has scrolled past.
  // The real hero boundary drives both bars, including jumps that skip an
  // IntersectionObserver threshold and browser-restored scroll positions.
  const [showStickyCta, setShowStickyCta] = useState(false)
  const heroEndRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const sentinel = heroEndRef.current
    if (!sentinel) return
    // FRIDGE-908: on phones the bar also shows while the buy box is still
    // below the first screen (assistant-referred landings never had a price
    // or a deal button in view without scrolling); desktop keeps "after hero".
    return observeHeroBoundary(sentinel, setShowStickyCta, {
      alsoWhenBelowViewport: () => typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 1023px)').matches,
    })
  }, [productData])

  // Sticky hero columns (desktop): pin the shorter of the two so the taller
  // one's scroll doesn't leave dead whitespace, and the buy box stays on
  // screen through the hero. Refs go on the two hero cells; the hook re-decides
  // on any height change (offers expand, description "read more", specs "show
  // all"). Returns null below lg / when the shorter column overflows the
  // viewport — see hooks/use-sticky-column.ts.
  const galleryColRef = useRef<HTMLDivElement>(null)
  const sidebarColRef = useRef<HTMLDivElement>(null)
  // `ready` re-measures once the hero columns mount — matters on the
  // client-only fallback path (initialData === null) where the hero renders
  // after data loads, not at first mount.
  const stickySide = useStickyColumn(galleryColRef, sidebarColRef, !!productData?.product)

  // Touch/swipe state for main image gallery
  const [touchStart, setTouchStart] = useState<number | null>(null)
  const [touchEnd, setTouchEnd] = useState<number | null>(null)

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50

  // Memoize product object to prevent random ratings from regenerating on every render
  const product = useMemo(() => {
    if (!productData?.product) {
      return null
    }
    
    const buildKeyFeatures = (prod: any) => {
      const features = []
      const specs = prod.specs || prod.specs_json || {}
      
      // Infer from title if data missing
      const title = (prod.title || '').toLowerCase()
      const inferred = {
        smart: title.includes('smart') || title.includes('wifi') || title.includes('wi-fi') || title.includes('app'),
        counterDepth: title.includes('counter depth') || title.includes('counter-depth'),
        stainless: title.includes('stainless') || title.includes('steel'),
        fingerprint: title.includes('fingerprint') || title.includes('printproof'),
        iceMaker: title.includes('ice maker') || title.includes('icemaker'),
        capacity: title.match(/(\d+\.?\d*)\s*cu\.?\s*ft/i)?.[1],
        // Niche inferences
        bottleCapacity: title.match(/(\d+)\s*-?\s*bottle/i)?.[1],
        canCapacity: title.match(/(\d+)\s*-?\s*can/i)?.[1],
        dualZone: title.includes('dual zone') || title.includes('dual-zone'),
        builtIn: title.includes('built-in') || title.includes('undercounter'),
        lock: title.includes('lock') || title.includes('security'),
        commercial: title.includes('commercial') || title.includes('nsf'),
        taps: title.match(/(\d+)\s*-?\s*tap/i)?.[1]
      }
      
      // Capacity (Volume, Bottles, Cans)
      if (prod.capacity || inferred.capacity) features.push(`${prod.capacity || inferred.capacity} cu. ft. capacity`)
      if (inferred.bottleCapacity) features.push(`Holds up to ${inferred.bottleCapacity} bottles`)
      if (inferred.canCapacity) features.push(`Holds up to ${inferred.canCapacity} cans`)
      
      // Energy Star
      if (specs.energy_star || title.includes('energy star')) features.push('Energy Star certified')
      
      // Ice maker (skip for wine/kegerators)
      if (specs.ice_maker || specs.has_ice_maker || inferred.iceMaker) features.push('Built-in ice maker')
      
      // Niche Features
      if (inferred.dualZone) features.push('Dual zone temperature control')
      if (inferred.builtIn) features.push('Built-in / Undercounter design')
      if (inferred.lock) features.push('Security lock with key')
      if (inferred.commercial) features.push('Commercial-grade construction')
      if (inferred.taps) features.push(`${inferred.taps}-tap draft system`)
      
      // Smart features
      if (specs.wifi_enabled || specs.smart_features || specs.app_control || inferred.smart) features.push('WiFi & Smart Home Compatible')
      
      // Counter Depth
      if (specs.installation === 'counter-depth' || inferred.counterDepth) features.push('Counter-Depth Design')
      
      // Finish
      if (inferred.stainless) features.push('Stainless Steel Finish')
      if (inferred.fingerprint) features.push('Fingerprint Resistant')
      
      // Cooling system
      if (specs.dual_cooling || specs.cooling_system) features.push(specs.cooling_system || 'Dual cooling system')

      // LED lighting — only push when explicitly true (May 10, 2026:
      // previous default-true-on-unset pinned a phantom "LED interior
      // lighting" on every keepa-bridged commercial PDP, e.g. Metro
      // C5R9-SB user-flagged screenshot).
      if (specs.led_lighting === true || title.includes('led')) features.push('LED interior lighting')

      // Removed May 10, 2026: when features.length === 0 we used to push
      // 3 generic filler items ("X design", "Modern appliance technology",
      // "Adjustable storage options"). That violates Real-or-Hidden Rule
      // 3.5 — boilerplate filler copy presented as real product features.
      // Now: return [] and let the render layer self-hide the section.

      // Deduplicate and return top 6
      return Array.from(new Set(features)).slice(0, 6)
    }

    // Deterministic fallback for rating/reviews based on product ID (prevents hydration mismatch)
    // Hash the product ID to get a stable number between 0-1
    const getStableHash = (str: string): number => {
      let hash = 0
      for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i)
        hash = ((hash << 5) - hash) + char
        hash = hash & hash // Convert to 32bit integer
      }
      return Math.abs(hash) / 2147483647 // Normalize to 0-1
    }
    
    const productId = productData.product.id || productData.product.model || 'default'
    const stableValue = getStableHash(String(productId))
    
    return {
      name: formatProductTitle(productData.product.title),
      title: productData.product.title, // Raw title for FAQ parsing
      model: productData.product.model,
      brand: productData.product.brand,
      brandPageActive: productData.product.brandPageActive === true,
      brandPageSlug: (productData.product.brandPageSlug as string | null | undefined) ?? null,
      type: productData.product.type,
      finish: productData.product.finish,
      capacity_cuft: productData.product.capacity,
      // Dimensions: run through the normalizer once at the boundary so every
      // downstream consumer (the 2x2 quick-specs grid at line ~1009, the
      // Design & Build prose at line ~630, the TechnicalSpecs block, the
      // ProductFAQ block) gets safe inch values. If the raw record is corrupted
      // (e.g. Samsung RF23DB9750QLAA: 912/1853/731 mm) the normalized fields
      // become null and downstream `width_in &&` ternaries hide the rows.
      // See lib/dimension-utils.ts. Bug 2026-05-07.
      ...(() => {
        const dims = normalizeDimensions(
          {
            width_in: productData.product.dimensions?.width,
            height_in: productData.product.dimensions?.height,
            depth_in: productData.product.dimensions?.depth,
          },
          productData.product.type,
        )
        return {
          width_in: dims.widthInches ?? undefined,
          height_in: dims.heightInches ?? undefined,
          depth_in: dims.depthInches ?? undefined,
        }
      })(),
      lowest_price_cents: productData.product.lowest_price_cents,
      specs_json: productData.product.specs,
      // Real-or-Hidden (Rule 3): pass through real Keepa-derived rating /
      // review counts ONLY. Earlier this fell back to a deterministic
      // 3.5-5.0 hash-of-product-id rating + 10-510 random review count to
      // "prevent hydration mismatch" — that produced fake stars on every
      // PDP without real Keepa rating data, violating "never fake."
      // The PDP renderers gate every star UI on `typeof rating === 'number'
      // && rating > 0` so null hides cleanly.
      rating: typeof productData.product.average_rating === 'number' && productData.product.average_rating > 0
        ? productData.product.average_rating
        : null,
      reviews: typeof productData.product.review_count === 'number' && productData.product.review_count > 0
        ? productData.product.review_count
        : null,
      images: productData.product.images || ["/placeholder.svg", "/placeholder.svg", "/placeholder.svg", "/placeholder.svg"],
      features: buildKeyFeatures(productData.product),
      price: productData.product.offers?.[0]?.price ? productData.product.offers[0].price / 100 : undefined,
      originalPrice: productData.product.offers?.[0]?.originalPrice ? productData.product.offers[0].originalPrice / 100 : undefined,
    }
  }, [
    // React Compiler infers these field-level dependencies for this memo. The
    // previous `[productData?.product]` was the parent object, which is coarser
    // — safe, but not something the compiler can prove equivalent, so it
    // reported "existing memoization could not be preserved" fifteen times.
    // Listing what it infers makes declared and inferred agree. The object
    // itself stays in the list, so any field not named here is still covered.
    productData?.product,
    productData?.product?.brand,
    productData?.product?.capacity,
    productData?.product?.dimensions?.depth,
    productData?.product?.dimensions?.height,
    productData?.product?.dimensions?.width,
    productData?.product?.finish,
    productData?.product?.images,
    productData?.product?.lowest_price_cents,
    productData?.product?.model,
    productData?.product?.specs,
    productData?.product?.title,
    productData?.product?.type,
  ])

  // ── Canonical product badge array (single source for this page) ───────────
  // Compute the badge set ONCE from the same canonical engine the cards use
  // (badgesFromTypes(determineBadges(...))), then feed the SAME array to both
  // the hero overlay (capped at 3) and the full inline row under the title
  // (complete set). This guarantees a product's badges are identical on its
  // detail page and on any card/collection/sidebar surface — the input shape
  // mirrors app/collections/[slug]/collection-client.tsx's ProductCardBadges
  // call so the two surfaces resolve the exact same membership.
  // The server-side `badges: Badge[]` field (BADGE_DATA_MODEL_SPEC §6) is now
  // emitted by BOTH the SSR page (initialData) and /api/products/[slug]; when
  // present it is used verbatim. The legacy client derivation below remains
  // only as a fallback for cached payloads that predate the field.
  // Hoisted out of the dependency array on purpose: an OPTIONAL-CHAINED member
  // expression (`[productData?.product]`) is not something the React Compiler
  // can map to a stable dependency, so it gave up on this file's manual
  // memoization entirely — `react-compiler/preserve-manual-memoization`. A plain
  // identifier is the same value with a shape the compiler can track.
  const productForBadges = productData?.product
  const catalogBadges = useMemo(() => {
    const p = productForBadges
    if (!p) return []
    if (Array.isArray(p.badges)) return p.badges
    // Best offer carries the retailer-level deal / availability signals.
    const bestOffer = p.offers?.[0] || {}
    return badgesFromTypes(
      determineBadges({
        lowest_price_cents: p.lowest_price_cents ?? undefined,
        average_price_30d_cents: p.average_price_30d_cents ?? undefined,
        lowest_price_ever_cents: p.lowest_price_ever_cents ?? undefined,
        created_at: p.created_at ?? undefined,
        specs_json: p.specs_json ?? p.specs ?? undefined,
        availabilityMaxQty: bestOffer.availabilityMaxQty,
        dealBadge: bestOffer.dealBadge,
        offers: (p.offers || []).map((o: any) => ({
          priceCents: o.price,
          originalPriceCents: o.originalPrice,
          priceStale: o.priceStale,
          inStock: o.inStock,
        })),
      }),
    )
  }, [productForBadges])

  // Image navigation functions
  const handlePreviousImage = () => {
    if (productData?.product?.images) {
      setSelectedImageIndex((prev) =>
        prev === 0 ? productData.product.images.length - 1 : prev - 1
      )
    }
  }

  const handleNextImage = () => {
    if (productData?.product?.images) {
      setSelectedImageIndex((prev) =>
        prev === productData.product.images.length - 1 ? 0 : prev + 1
      )
    }
  }

  // Touch handlers for swipe
  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null)
    setTouchStart(e.targetTouches[0].clientX)
  }

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX)
  }

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return

    const distance = touchStart - touchEnd
    const isLeftSwipe = distance > minSwipeDistance
    const isRightSwipe = distance < -minSwipeDistance

    if (isLeftSwipe) {
      handleNextImage()
    } else if (isRightSwipe) {
      handlePreviousImage()
    }
  }

  // Only fetch client-side if no initial data was provided (fallback for direct navigation)
  useEffect(() => {
    // Skip fetch if we already have data from server (ISR)
    if (initialData) return
    
    const fetchProduct = async () => {
      if (!slug) return
      try {
        setLoading(true)
        setNotFound(false)
        // Use fetchWithDelay to ensure BotID signals are collected before API call
        const response = await fetchWithDelay(`/api/products/${slug}${preview ? '?preview=1' : ''}`)
        
        // Handle 404 - show not found state
        if (!response.ok) {
          setNotFound(true)
          return
        }
        
        const data = await response.json()
        
        // Handle redirect to SEO-friendly URL
        if (data.redirect && data.redirect.destination) {
          window.location.replace(data.redirect.destination)
          return
        }
        
        if (data.product) {
          setProductData(data)
        } else {
          // Product not found - show not found state
          setNotFound(true)
        }
      } catch (err: any) {
        // Silently handle - secondary data is optional
        // On error - show not found state
        setNotFound(true)
      } finally {
        setLoading(false)
      }
    }
    fetchProduct()
  }, [slug, initialData])

  // Fetch brands for "Brands In This Category" section - MATCHES COLLECTION PAGE
  // FRIDGE-1524: only when the server did not answer this row.
  useEffect(() => {
    if (serverBrands) return
    const fetchBrands = async () => {
      try {
        // Use fetchWithDelay to ensure BotID signals are collected before API call
        const response = await fetchWithDelay('/api/brands')
        if (response.ok) {
          const data = await response.json()
          setBrands(data.brands || [])
        }
        // Silently handle non-OK responses - brands are optional
      } catch {
        // Silently handle - brands are optional enhancement
        setBrands([])
      }
    }
    fetchBrands()
  }, [serverBrands])

  // FRIDGE-1623: "Show More" and the brand search load every partner brand
  // once, through the same read the page used before the server sent the
  // first tiles. A failed read leaves the first tiles in place.
  const loadAllBrands = useCallback(() => {
    if (allBrandsLoaded || brandsLoadStarted.current) return
    brandsLoadStarted.current = true
    fetchWithDelay('/api/brands')
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (Array.isArray(data?.brands) && data.brands.length > 0) {
          setBrands(data.brands)
          setAllBrandsLoaded(true)
        }
      })
      .catch(() => {})
  }, [allBrandsLoaded])

  // Fetch related products (used for analytics/dedup)
  useEffect(() => {
    const fetchRelated = async () => {
      if (!productData?.product) return
      const product = productData.product
      try {
        const related = await getRelatedProducts(product.type, product.id, 4)
        const hasRealImage = (p: RecommendedProduct) => Array.isArray(p.images) && p.images.length > 0 && isRenderableImageUrl(p.images[0])
        setRelatedProducts(related.filter(hasRealImage))
      } catch {
        // Silently handle - related products are optional
      }
    }
    fetchRelated()
  }, [productData])

  // Fetch the two surviving featured rails in parallel (2026-07-08 reorder):
  // Slot B "Closest alternatives" (scoped: type + price band + form_factor,
  // de-duped against the on-page comparison table) and Slot C "Top-rated"
  // (reuses the featured-with-fallback source). Trending / best-value /
  // bestsellers fetches were removed with their rails.
  // FRIDGE-1524: the server reads both rails (page.tsx); a rail it answered is
  // in the server HTML already, so the browser reads only a rail whose server
  // read failed, and both on the client-only fallback path.
  useEffect(() => {
    const needClosest = !serverClosest
    const needTopRated = !serverTopRated
    if (!needClosest && !needTopRated) return
    const fetchCarousels = async () => {
      if (!productData?.product) return
      const p = productData.product
      const collectionName = formatCollectionName(p.type)
      const hasRealImage = (item: RecommendedProduct) =>
        Array.isArray(item.images) && item.images.length > 0 && isRenderableImageUrl(item.images[0])
      const safe = <T,>(promise: Promise<T>, fallback: T): Promise<T> => promise.catch(() => fallback)

      // Product ids already shown in the comparison table — Slot B de-dupes
      // these so the same alternative never appears twice on one page.
      const compareIds = (((p as any).compareTargets || []) as Array<{ product_id?: string | null }>)
        .map((t) => t.product_id)
        .filter((id): id is string => typeof id === 'string' && id.length > 0)

      const [closest, featured] = await Promise.all([
        needClosest
          ? safe(
              getClosestAlternatives({
                excludeProductId: p.id,
                type: p.type,
                formFactor: Array.isArray((p as any).form_factor) ? (p as any).form_factor : null,
                priceCents: typeof p.lowest_price_cents === 'number' ? p.lowest_price_cents : null,
                excludeProductIds: compareIds,
                limit: 12,
              }),
              [] as RecommendedProduct[]
            )
          : null,
        needTopRated ? safe(getFeaturedProductsWithFallback(p.type, collectionName, 18), null) : null,
      ])

      if (needClosest) setClosestAltProducts((closest || []).filter(hasRealImage))
      if (needTopRated) setTopRatedProducts((featured?.products || []).filter(hasRealImage))
    }
    fetchCarousels()
  }, [productData, serverClosest, serverTopRated])

  // When rendered from server (ISR), we have initialData - don't show loading
  // Loading state only appears for client-only navigation (rare edge case)
  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-12">
          <p className="text-gray-600">Loading product details...</p>
        </div>
      </div>
    )
  }

  // Error state - server already renders static content, so this is a fallback
  if (notFound || error || !productData) {
    return (
      <div className="container mx-auto px-4 py-8">
        <NotFoundState section="product" />
      </div>
    )
  }

  // Clean up brand names for display. FRIDGE-348: the canonical casing map
  // (lib/brand-casing.ts) answers first — "Lg Studio" → "LG Studio", "Lg
  // Electronics" → "LG Electronics"; the legacy heuristics below only ever
  // run for a brand the map does not know.
  const cleanBrandName = (brand: string) => {
    if (!brand) return ''
    if (isKnownBrand(brand)) return displayBrandName(brand)

    // If all lowercase and short (likely abbreviation), uppercase it
    if (brand.length <= 5 && brand === brand.toLowerCase()) {
      return brand.toUpperCase()
    }

    // Handle special cases
    const cleaned = brand
      .replace(/\b(lg|ge|lg|keg|sub|avanti|ge|rca|igloo)\b/gi, match => match.toUpperCase())
      .trim()

    return cleaned
  }

  // 57% of legacy products carry a raw Amazon ASIN in `model` (audit
  // 2026-06-12). An ASIN is never a model number a shopper can use — prefer
  // the canonical manufacturer model for display when we have one. Also guards
  // the `__PENDING_REVIEW__` canonical-mint placeholder (2026-07-03 leak):
  // resolveDisplayModel returns '' when nothing displayable exists, and every
  // render site must handle the empty string (hide the Model row / drop the
  // model phrase) — never print the sentinel.
  const displayModel = resolveDisplayModel(
    product?.model,
    productData?.product?.manufacturerModel,
  )

  // FRIDGE-134: 518 groups of live products shared a title, so 1,189 PDPs
  // rendered a byte-identical H1 with nothing naming which product it was.
  // The H1 carries the row's own model when the feed title omits it, and uses
  // the same helper as the <title> keyword segment so the two never disagree.
  // resolveTitleModel, not displayModel: both now reject a feed-prefixed ASIN
  // (FRIDGE-110 closed that leak in the shared resolver on 08/16/2026), and the
  // two rules are pinned equal by test so they cannot drift apart.
  // FRIDGE-538 — a part or filter gets a heading derived from what it IS, not
  // the marketplace title it was imported with. 73% of live part/filter titles
  // carry a compatibility list (against 0.13% of appliance titles), several are
  // hard-capped at 200 chars and cut mid-word, and a fits-list belongs in the
  // "Fits these refrigerators" block (FRIDGE-397), not in the <h1>.
  //
  // It returns null when it cannot build one HONESTLY — 237 of 434 rows store
  // an ASIN rather than a real part number — and the appliance path then runs
  // unchanged. Falling back to a stuffed title beats printing an ASIN or
  // emitting the same heading on every Icepure filter.
  //
  // FRIDGE-547: when `model` is an ASIN there is no manufacturer part number to
  // print — and none exists anywhere in our data (upc 0, normalized_model 0, no
  // `mpn` key). What we DO have is `specs_json.oem_filter_type`: the OEM part
  // this REPLACES. Said as "for MWF" it is a compatibility claim, not a false
  // identity, and it lifts the honest-heading path from 197 to 414 of 434.
  const nonApplianceSpecs = (product?.specs_json ?? {}) as Record<string, unknown>
  const nonApplianceTitle = buildNonApplianceDisplayTitle({
    listingTitle: productData?.product?.title,
    brand: product?.brand,
    type: product?.type,
    model: product?.model,
    oemFilterType: typeof nonApplianceSpecs.oem_filter_type === 'string' ? nonApplianceSpecs.oem_filter_type : null,
    packSize:
      typeof nonApplianceSpecs.pack_size === 'string' || typeof nonApplianceSpecs.pack_size === 'number'
        ? nonApplianceSpecs.pack_size
        : null,
  })
  const displayTitle =
    nonApplianceTitle ??
    buildProductDisplayTitle(
      product?.name,
      resolveTitleModel(product?.model, productData?.product?.manufacturerModel),
    )

  // FRIDGE-333 — which family this page is. Decided from the row's TYPE
  // through the one helper the server used (lib/parts/fpn.ts), never from the
  // URL, so the SSR path (initialData.family/pagePath from page.tsx) and the
  // client-only fallback path (/api/products payload, no family field) agree.
  // A part shows its (FPN) Fridge.com Part Number and never a FIN; an
  // appliance shows its (FIN) and never an FPN.
  const productIsPart = isPartType(productData?.product?.type)
  const clientFamily = familyForType(productData?.product?.type)
  const pagePath: string =
    (typeof productData?.pagePath === 'string' && productData.pagePath) ||
    `${familyRoot(clientFamily)}/${slug}`

  // Generate product analytics
  const analysis = product ? analyzeProduct({
    ...productData.product,
    price: product.price,
    originalPrice: product.originalPrice,
    capacity_cuft: product.capacity_cuft,
    rating: product.rating,
    reviews: product.reviews
  }, relatedProducts) : null

  const retailers = (productData?.product?.offers || []).map((offer: any) => {
    // The server applies the owner-approved human-visible freshness window.
    // Missing, invalid, nonpositive and stale prices keep the update-pricing
    // link but cannot become a current-price recommendation.
    // FRIDGE-1643: the one shared rule, which the Product JSON-LD's offer
    // filter also calls, so the schema lists exactly the priced rows.
    const isPriceUnknown = isOfferPriceUnavailable(offer);

    return {
      id: offer.id,
      name: offer.retailer,
      priceCents: typeof offer.price === 'number' && offer.price > 0 ? offer.price : undefined,
      priceDisplay: isPriceUnknown ? 'Price Unavailable' : offer.priceFormatted,
      isPriceUnknown: isPriceUnknown,
      isStale: offer.priceStale === true,
      lastChecked: (offer.lastChecked as string | null) ?? null,
      originalCents: typeof offer.originalPrice === 'number' ? offer.originalPrice : undefined,
      originalDisplay: offer.originalPriceFormatted,
      shipping: offer.promoText,
      logo: "/placeholder.svg",
      // OffersV2 fields
      savingsPercentage: offer.savingsPercentage,
      savingsBasisLabel: offer.savingsBasisLabel,
      dealBadge: offer.dealBadge,
      dealType: offer.dealType,
      dealAccessType: offer.dealAccessType,
      dealEndsAt: offer.dealEndsAt,
      dealPercentClaimed: offer.dealPercentClaimed,
      merchantName: offer.merchantName,
      isBuyBoxWinner: offer.isBuyBoxWinner,
      availabilityMaxQty: offer.availabilityMaxQty,
      pricePerUnit: offer.pricePerUnit,
      // FRIDGE-145: commission rates are NOT carried here. They are a
      // commercial term, and anything on this object is serialized into the
      // public RSC payload. The server's price/freshness adapter also omits
      // rates; only the selected offer ID and its basis accompany the rail.
    }
  })
  // P1 sort: verified fresh prices first (ascending), stale/unknown demoted —
  // a frozen "$1,899" must never outrank a real "$2,394".
  .sort((a: any, b: any) => {
    if (a.isPriceUnknown !== b.isPriceUnknown) return a.isPriceUnknown ? 1 : -1
    return (a.priceCents ?? Number.MAX_SAFE_INTEGER) - (b.priceCents ?? Number.MAX_SAFE_INTEGER)
  })

  // P1 buybox: which offer the buy box, verdict CTA, sticky CTA and compare
  // CTA all point at; price-history badge from keepa_price_snapshots
  // (page-side stats).
  //
  // FRIDGE-117: server and client choose from genuine fresh prices without
  // commercial ranking inputs. A cached server ID remains usable only while
  // it matches a current lowest fresh price. Old or stale recommendations
  // cannot override the visible rail's price/freshness ordering.
  const serverPickedOffer = (productData?.product as any)?.buyBoxOfferId
    ? (retailers as any[]).find((r: any) => r.id === (productData?.product as any).buyBoxOfferId)
    : null
  const buyBoxSelection = selectBuyBox(retailers as any[])
  const bestFreshOffer = canUseServerBuyBox(serverPickedOffer, buyBoxSelection.offer)
    ? serverPickedOffer : buyBoxSelection.offer
  const priceCtx = (productData?.product as any)?.priceContext as
    | { avg_cents: number; min_cents: number; sample_days: number }
    | null
  // FRIDGE-792: computed server-side beside priceContext; null under the floor.
  const saleFrequency = ((productData?.product as any)?.saleFrequency ?? null) as SaleFrequency | null
  // FRIDGE-760: one derivation, every retailer CTA on the page reads it — with an
  // active CPSC recall the buy buttons drop to secondary and "Learn about this
  // recall" takes the primary tone (lib/product/recall-cta.ts).
  const recallsForFin: FinRecallRow[] | undefined = productData?.product?.recallsForFin
  const hasActiveRecall = deriveHasActiveRecall(recallsForFin)
  // FRIDGE-874 — ONE row shape for this product, shared by the buy box control
  // and both sticky bars, so all three write the same entry in the Saved/Compare
  // store (and the header badge) rather than three near-duplicates.
  const pdpSavedItem = buildSavedItem({
    id: productData.product.id,
    fin: (productData.product as any)?.fin,
    brand: productData.product.brand,
    title: displayTitle || product.name,
    image: product.images?.[0],
    href: pagePath,
    priceCents: bestFreshOffer?.priceCents,
  })
  const priceContextBadge: string | null = (() => {
    if (!priceCtx || !bestFreshOffer?.priceCents) return null
    if (bestFreshOffer.priceCents <= priceCtx.min_cents) return 'Lowest price we’ve tracked in 90 days'
    const belowAvg = priceCtx.avg_cents - bestFreshOffer.priceCents
    if (belowAvg >= priceCtx.avg_cents * 0.03) {
      return `${formatCents(belowAvg)} below the 90-day average`
    }
    return null
  })()
  // P05: flag-gated full-history deal verdict (page-side stats). When it
  // renders a positive tier it supersedes the 90-day badge — the two would
  // otherwise say the same thing twice.
  const dealVerdictData = (productData?.product as any)?.dealVerdict as
    | DealVerdictData
    | null
  const showDealVerdict = dealVerdictModuleEnabled() && !!dealVerdictData
  const dealVerdictSupersedesBadge =
    showDealVerdict &&
    (dealVerdictData!.tier === 'all-time-low' || dealVerdictData!.tier === 'below-average')

  // Lane: the richer Price-intelligence module. It recomputes the verdict
  // against the LIVE displayed price (bestFreshOffer) — not the last tracked
  // snapshot — so "Current" and the verdict agree, and it self-hides on
  // See-Deal / price-unknown products (derive gate 1). When it renders it is
  // the single price-history surface, superseding the standalone DealVerdict
  // pill and the 90-day priceContextBadge so the page never says it thrice.
  const priceSparkline = (productData?.product as any)?.priceSparkline as SparkPoint[] | null
  // FRIDGE-1253: standalone stats first — dealVerdict is null below the 14-day
  // claim floor, which used to leave the card saying tracking had just started.
  const priceIntelStats = priceIntelStatsFor(productData?.product as any)
  // FRIDGE-583 (owner, 08/27/2026): the module is unconditional — the env
  // kill-switch was removed with the ruling "needs to always be there no
  // matter whatever happens ever". derive returns null only on a priceless
  // product, which a live PDP is not.
  const priceIntelVM = derivePriceIntelligence({
    currentCents: bestFreshOffer?.priceCents ?? null,
    stats: priceIntelStats,
    series: priceSparkline,
  })
  const showPriceIntel = !!priceIntelVM
  const productBadges = priceEvidenceBadges(catalogBadges, bestFreshOffer, priceIntelVM)

  // ── Derived data for the new sections (matches test page layout) ──────────
  const productTypeName = formatProductType(product.type) || 'Refrigerator'
  // Phase-2-aligned plural display name for carousel headlines (e.g. "Compact Refrigerators",
  // "French Door Refrigerators", "Mini Fridges"). Bypasses formatCollectionName which falls
  // back to a bare title-case for types like 'compact' or 'built-in' and yields headlines
  // missing the category noun (e.g. "Best Value Compact" instead of "Best Value Compact
  // Refrigerators"). See lib/url-format.ts for the mapping.
  const collectionDisplayPlural = getCategoryDisplayNamePlural(product.type)
  const isFreezer = (product.type || '').toLowerCase().includes('freezer')
  const productNoun = isFreezer ? 'freezer' : 'refrigerator'
  const finishStr = product.finish || (analysis?.features.specialFeatures.find(f => f.toLowerCase().includes('stainless')) ? 'stainless steel' : 'modern')
  const widthStr = product.width_in ? `${product.width_in}"` : null
  const capacityStr = product.capacity_cuft ? `${product.capacity_cuft} cu. ft.` : null
  const energyStar = analysis?.features.isEnergyStarCertified

  // (Detailed Buying Guide construction removed — conversion spec P0; see
  // the render-site comment near #related-resources.)

  // Related resources — server-side `getHubsForProduct` returns only hub_pages
  // rows with status='active', so every slug here is guaranteed to render.
  // Dedupe by slug as a safety net in case the server ever sends duplicates.
  const guideMap = new Map<string, { slug: string; title: string; hub_type?: string | null }>()
  ;(relatedHubs as Array<{ slug?: string; title?: string; hub_type?: string | null }>).forEach((g) => {
    if (g?.slug && g?.title && !guideMap.has(g.slug)) {
      guideMap.set(g.slug, { slug: g.slug, title: g.title, hub_type: g.hub_type ?? null })
    }
  })
  const relatedGuides = Array.from(guideMap.values()).slice(0, 8)

  // "What's In The Box" + "Warranty & Support / Parts & Manuals" REMOVED
  // (owner note 07-06): both were derived boilerplate. Real manufacturer
  // manuals/guides now live in the ProductDocuments "Manuals & Guides"
  // section (canonical_products.pdfs, verified/hosted).

  return (
    <>
      {/* Product Tracking - tracks view on mount */}
      {productData?.product && (
        <ProductTracking
          productId={productData.product.id}
          productName={productData.product.title}
          productType={productData.product.type}
          productBrand={productData.product.brand}
          source="product_page"
        />
      )}

      {/* NOTE: SEO Structured Data (ProductSchemas) is now rendered by the Server Component */}
      {/* This client component handles interactive features only */}

      <div className="container mx-auto px-2 sm:px-4 py-4 sm:py-8">
        {/* BREADCRUMB - Fridge.com / {spine} / {type} / Product Name (FRIDGE-57).
            ONE trail (FRIDGE-269): the server builds it once with
            lib/breadcrumbs.ts productBreadcrumbs() and ships it as
            initialData.breadcrumbs — the SAME array the BreadcrumbList schema
            in page.tsx emits, so the visible names can never disagree with
            it (until FRIDGE-269 this block resolved its own label and fell
            back to singular "Refrigerator" → /collections/all while the
            schema said "Counter-Depth Refrigerators"). The client-only
            fallback path (initialData === null, /api/products) rebuilds the
            trail from the same product fields with the same helper
            (lib/breadcrumbs-product.ts, which keeps the collection parser out
            of this bundle). Each collection crumb's label and href derive from
            one slug (FRIDGE-271); when nothing maps the level is omitted,
            never invented. */}
        <Breadcrumb
          trail={
            (productData.breadcrumbs as BreadcrumbTrail | undefined) ??
            productBreadcrumbs({ type: productData.product.type, title: productData.product.title, leafName: nonApplianceTitle })
          }
          className="mb-4 sm:mb-6 px-2 sm:px-0"
          listClassName="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs sm:text-sm text-gray-600 list-none m-0 p-0"
          itemClassName="flex items-center gap-x-2 min-w-0 max-w-full"
          linkClassName="hover:text-blue-600"
          currentClassName="text-gray-900 truncate max-w-[200px] sm:max-w-[400px]"
          separatorClassName=""
        />

        {/* HERO SECTION — 58/42 split matching /product-detail-view-test.
            lg:items-start is REQUIRED for the sticky columns below: flex
            children default to `stretch` (equal height), and position:sticky
            inside a container it already fills is a no-op. */}
        <div className="flex flex-col lg:flex-row lg:items-start gap-8">
          {/* IMAGE GALLERY - Full width on mobile, 58% on desktop. Sticky when
              the hook picks the gallery as the shorter column. */}
          <div
            ref={galleryColRef}
            className={`lg:w-[58%] mb-4 sm:mb-6 lg:mb-0${stickySide === 'left' ? ' lg:sticky lg:top-28 lg:self-start' : ''}`}
          >
            {/* Image Gallery with working thumbnails */}
            <div className="flex gap-2 sm:gap-4">
              {/* Thumbnail Column - Vertical strip on LEFT like Amazon */}
              <div className="hidden sm:flex flex-col gap-2 w-16 sm:w-20">
                {product.images.slice(0, 6).map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImageIndex(index)}
                    // FRIDGE-1508 K1508-16: the current image is said, not only shown by colour.
                    aria-current={selectedImageIndex === index ? 'true' : undefined}
                    className={`aspect-square overflow-hidden rounded-md border-2 transition-colors min-h-[44px] ${
                      selectedImageIndex === index ? 'border-blue-600' : 'border-gray-300 hover:border-blue-400'
                    }`}
                  >
                    <SafeImage
                      src={image || "/placeholder.svg"}
                      fallbackContent={clientFamily === 'filters' ? <FilterBrandTile brand={product.brand} logo={null} size="xs" /> : undefined}
                      alt={`${product.name} - View ${index + 1} | Fridge.com`}
                      width={80}
                      height={80}
                      className="w-full h-full object-contain p-1"
                      loading="lazy"
                    />
                  </button>
                ))}
              </div>

              {/* Main Image - Click thumbnail changes this */}
              <div className="flex-1">
                <div
                  className="relative aspect-square product-image-canvas overflow-hidden rounded-lg border group"
                  onTouchStart={onTouchStart}
                  onTouchMove={onTouchMove}
                  onTouchEnd={onTouchEnd}
                >
                  {/* Hero Badges - Stay visible as images change.
                      Fed the SAME canonical array as the full row under the
                      title, so the overlay (top 3 by priority) is always a
                      strict subset of the page's complete badge set. */}
                  <ProductHeroBadges
                    badges={productBadges}
                    position="left"
                    maxBadges={3}
                  />
                  
                  {/* FRIDGE-635 (owner, 08/29/2026): "if no photo use logo of
                      brand" — an empty or dead hero slot renders the product's
                      BRAND logo (the LG mark on the LT1000P, his example), and
                      only falls to the Fridge.com placeholder when the brand
                      has no logo asset or it fails too. Filters use their
                      deliberate brand tile instead (FRIDGE-1152). */}
                  <BrandedImage
                    src={product.images[selectedImageIndex] || ""}
                    fallbackContent={productData.product.localPreviewMissingImage ? (
                      <div className="flex h-full min-h-80 w-full items-center justify-center bg-white p-8 text-center text-slate-600">
                        <div><p className="font-semibold">The stored product photo is unavailable.</p><p className="mt-2 text-sm">Its retailer source returns 404. No replacement photo has been added to the Before view.</p></div>
                      </div>
                    ) : clientFamily === 'filters' ? (
                      <div role="img" aria-label={`${product.name} — product photo unavailable`} className="h-full w-full">
                        <FilterBrandTile brand={product.brand} logo={(product as any).brandStory?.logoPath} code={productData.product.model} />
                      </div>
                    ) : undefined}
                    brandLogoSrc={(product as any).brandStory?.logoPath ?? undefined}
                    productName={product.name}
                    width={600}
                    height={600}
                    className="w-full h-full object-contain p-4 sm:p-6"
                    // FRIDGE-1400: the main slot is always in view, so it never waits on lazy
                    // loading, and the first photo, the page's largest paint, is fetched first.
                    // Next 16's deprecated `priority` gave a preload link but no fetchpriority.
                    loading="eager"
                    fetchPriority={selectedImageIndex === 0 ? 'high' : undefined}
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 600px"
                  />

                  {/* Navigation Arrows - Always visible on mobile, show on hover on desktop.
                      FRIDGE-1508 K1508-5: and on keyboard focus — of either arrow, or of
                      anything else in the gallery (the share button). They were
                      opacity 0 from 640 px with nothing on focus, so Tab landed on an
                      invisible control. K1508-7: in forced colors they always show,
                      with a system-colour edge (the shadow that outlines them is
                      dropped there). */}
                  {product.images.length > 1 && (
                    <>
                      <button
                        onClick={handlePreviousImage}
                        className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-white/90 hover:bg-white rounded-full shadow-lg max-sm:opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:group-has-[:focus-visible]:opacity-100 sm:focus-visible:opacity-100 forced-colors:opacity-100! forced-colors:border forced-colors:border-[color:ButtonText] transition-opacity min-w-[44px] min-h-[44px] flex items-center justify-center z-10"
                        aria-label="Previous image"
                      >
                        <ChevronLeft className="w-5 h-5 text-gray-700" />
                      </button>
                      <button
                        onClick={handleNextImage}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-white/90 hover:bg-white rounded-full shadow-lg max-sm:opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:group-has-[:focus-visible]:opacity-100 sm:focus-visible:opacity-100 forced-colors:opacity-100! forced-colors:border forced-colors:border-[color:ButtonText] transition-opacity min-w-[44px] min-h-[44px] flex items-center justify-center z-10"
                        aria-label="Next image"
                      >
                        <ChevronRight className="w-5 h-5 text-gray-700" />
                      </button>
                    </>
                  )}

                  {/* Share sheet on the hero image (FRIDGE-344): the round
                      button opens a light sheet — copy, email, X, Facebook,
                      Pinterest, WhatsApp, Instagram/more — every link a
                      fridge.com URL, every control's tooltip naming the
                      product and the brand (FRIDGE-352). It positions itself
                      top-right inside this container. */}
                  <ProductImageShareSheet
                    name={displayTitle || product.name}
                    path={pagePath}
                    imageUrl={product.images?.[selectedImageIndex] || null}
                  />
                </div>
                {/* Mobile: Clickable dots that change image.
                    FRIDGE-1508 K1508-7: in forced colors (Windows high contrast)
                    the dots were background colour only, which the browser
                    replaces with the page colour, so all of them vanished. There
                    they keep their own system colours: an outlined dot, and the
                    current one filled with the selection colour.
                    FRIDGE-1507 T: each 10px dot taps as 24 x 24 (tap-24), and
                    the dots sit 24px apart centre to centre (gap 14px, was 10)
                    so no two hit areas overlap (WCAG 2.2 SC 2.5.8).
                    FRIDGE-1586: the current dot is also a 24px pill, so it
                    differs by shape, not by colour alone (WCAG 1.4.1). One pill
                    and the same dots whichever image is current, so the centred
                    row keeps its width; the pill's hit area is its own width. */}
                <div className="flex sm:hidden justify-center gap-3.5 mt-3">
                  {product.images.slice(0, 6).map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImageIndex(index)}
                      // FRIDGE-1508 K1508-16: the current dot is said, not only shown by colour.
                      aria-current={selectedImageIndex === index ? 'true' : undefined}
                      className={`tap-24 h-2.5 rounded-full transition-[width,background-color] motion-reduce:transition-none forced-colors:[forced-color-adjust:none] forced-colors:border ${
                        selectedImageIndex === index ? 'w-6 bg-[#0469f0] forced-colors:border-[color:Highlight] forced-colors:bg-[color:Highlight]' : 'w-2.5 bg-gray-400 hover:bg-gray-700 forced-colors:border-[color:ButtonText] forced-colors:bg-[color:Canvas]'
                      }`}
                      aria-label={`View image ${index + 1}`}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Technical Specs — desktop: sits in the gallery column at the
                same level as the offers card (owner note 07-06; replaces the
                small quick-spec tiles). Mobile/tablet keeps specs in the page
                flow below the hero — see the lg:hidden instance. */}
            <div className="hidden lg:block mt-6 scroll-mt-28" id="specifications">
              {/* FRIDGE-149 §2: titleAs="p" — this instance precedes the H1 in
                  DOM order, so its title must not be a heading (the lg:hidden
                  instance below the hero keeps the real H2). */}
              <TechnicalSpecs
                product={product}
                productData={productData}
                analysis={analysis}
                extraSpecs={(productData.product as any)?.displaySpecs ?? null}
                titleAs="p"
              />
              {/* FRIDGE-280: dimension drawings / installation / parts
                  diagrams extracted from this FIN's banked manuals — the
                  visual twin of the specs table, so it sits directly under
                  it. Self-hides when no hosted assets exist. */}
              <ManualDiagrams
                diagrams={(productData.product as any)?.displayManualDiagrams}
                className="mt-4"
              />
            </div>
          </div>

          {/* PRODUCT INFO - 42% sidebar — matches /product-detail-view-test.
              Sticky when the hook picks the sidebar as the shorter column (the
              common case — this keeps the buy box on screen through the hero). */}
          <div
            ref={sidebarColRef}
            className={`lg:w-[42%] flex flex-col${stickySide === 'right' ? ' lg:sticky lg:top-28 lg:self-start' : ''}`}
          >
            {/* Brand + Rating inline with dot separator */}
            <div className="mb-2 flex items-center gap-2 flex-wrap">
              {/* Real-or-Hidden: only link to /brands/[slug] when that page
                  resolves (brandPageActive, computed server-side). Brands with
                  no page — unrecognized or thin — render as plain text so the
                  PDP never emits a 404 internal link. */}
              {product.brandPageActive && product.brandPageSlug ? (
                <Link
                  href={`/brands/${product.brandPageSlug}`}
                  className="text-[#0469f0] font-bold text-sm tracking-tight uppercase hover:underline"
                >
                  {cleanBrandName(product.brand)}
                </Link>
              ) : (
                <span className="text-[#0469f0] font-bold text-sm tracking-tight uppercase">
                  {cleanBrandName(product.brand)}
                </span>
              )}
              {typeof product.rating === 'number' && product.rating > 0 && (
                <>
                  <span className="w-1 h-1 rounded-full bg-gray-300" />
                  <div className="flex items-center gap-1 text-[#ff9951]">
                    <Star className="w-4 h-4 fill-current" />
                    <span className="text-xs font-bold text-black">
                      {product.rating.toFixed(1)}
                      {typeof product.reviews === 'number' && product.reviews > 0
                        ? ` (${product.reviews.toLocaleString()} reviews)`
                        : ''}
                    </span>
                  </div>
                </>
              )}
              {displayModel && (
                <>
                  <span className="w-1 h-1 rounded-full bg-gray-300" />
                  <span className="text-xs text-gray-600">Model: {displayModel}</span>
                </>
              )}
            </div>

            {/* H1 Title — large & bold */}
            <h1 className="text-2xl lg:text-3xl font-black text-gray-900 leading-tight tracking-tight mb-2">
              {displayTitle}
            </h1>

            {/* Identifier pill + the complete canonical badge set on ONE flex row
                (owner note 07-20): the identity anchor and the Price Drop / Energy
                Star chips read as a single identity strip under the title,
                wrapping only when they truly overflow (375px). Both children
                self-hide (the badge when its identifier is null, BadgeList when
                empty), and the row itself is omitted when neither exists.
                FRIDGE-333: a PART renders its (FPN) Fridge.com Part Number here
                and never a FIN — page.tsx nulls `fin` on a part before it
                reaches this payload, and `fpn` exists only on parts (products.fpn
                is CHECK-bound to part types), so the two chips are exclusive by
                construction, not by luck. */}
            {(() => {
              const p = productData.product as any
              const fin: string | null = clientFamily === 'products' ? (p?.fin ?? null) : null
              const fpn: string | null = clientFamily === 'parts' ? (p?.fpn ?? null) : null
              const ffn: string | null = clientFamily === 'filters' ? (p?.ffn ?? null) : null
              if (!fin && !fpn && !ffn && productBadges.length === 0) return null
              return (
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  {fpn && <FpnBadge fpn={fpn} />}
                  {ffn && <FfnBadge ffn={ffn} />}
                  {fin && <FinBadge fin={fin} />}
                  {productBadges.length > 0 && (
                    <BadgeList badges={productBadges} variant="full" />
                  )}
                </div>
              )
            })()}

            {/* Phase 10.5: visible "Updated {Month YYYY}" stamp.
                Reads fin_freshness.lastmod (cached daily by
                commerce-fin-freshness-populator). Self-hides when lastmod is
                null OR fresher than 30 days — per plan, current-month
                timestamps on every PDP every day would imply daily curation
                we don't do. Real-or-Hidden Rule 3. */}
            <div className="mb-3">
              <UpdatedStamp
                lastmod={(productData.product as any)?.finFreshnessLastmod || null}
                nowMs={(productData.product as any)?.finFreshnessNowMs ?? 0}
              />
            </div>


            {/* Phase 6: Active CPSC recall banner.
                Reads fin_recalls (populated by commerce-cpsc-daily-sweep cron),
                rendered ABOVE the price/CrossRetailerPriceWidget block because
                a safety recall takes precedence over commercial signals.
                Component self-hides when there are no active recalls
                (Real-or-Hidden Rule 3). */}
            <RecallBanner
              recalls={recallsForFin}
            />

            {/* Recall-alert opt-in RELOCATED below the offers card (owner note
                07-20): the money zone leads the column; the recall subscribe box
                must not push the price/CTA down. See its new slot directly under
                Current Offers. The active-recall RecallBanner above stays put —
                a live safety recall keeps precedence over commercial signals. */}

            {/* Cross-retailer pricing widget REMOVED (conversion spec P0):
                its Google-Shopping snapshot data was validated as junk
                (06/2026) and its "View on {retailer}" links pointed at
                NON-AFFILIATE retailers — raw exits in the money zone.
                Affiliate offers below are the only outbound price links. */}

            {/* === CURRENT OFFERS — orange tinted card ===
                Moved ABOVE the description (owner note 07-08): the buy box is
                the money zone and belongs at the top of the sidebar, not
                below a two-paragraph narrative. Description + share buttons
                now render directly under it. */}
            <div id="current-offers" className="bg-[#ff9951]/[0.06] rounded-lg p-5 mb-5 scroll-mt-28">
              {/* Owner ruling (FRIDGE-78, 07/25): the visible "Price checked
                  N ago" freshness stamp was removed sitewide. last_checked_at
                  still gates stale-price display upstream (priceStale /
                  offer honesty in structured data) — only the indicator went. */}
              <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
                {/* FRIDGE-149 §2: H2 (was H3 — skipped a level straight after the H1). */}
                <h2 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                  <Zap className="w-4 h-4 text-[#0469f0]" />
                  Current Offers
                </h2>
                {/* FRIDGE-760: with an active recall the primary action here is
                    the recall, not the purchase. */}
                {hasActiveRecall && <LearnAboutRecallLink />}
              </div>

              {/* FRIDGE-874 — Save + Compare for THIS product. Same store as the
                  header buttons, the collection cards and the compare tray, so
                  the header badge moves the instant the tick lands. */}
              <div className="mb-4 pb-3 border-b border-[#ff9951]/25">
                <SaveCompareRow item={pdpSavedItem} />
              </div>

              {/* FRIDGE-1003: reference-price savings cannot establish a historical low. */}
              <CurrentOfferDiscount offer={bestFreshOffer} />

              {/* P1: honest price-history urgency (keepa_price_snapshots) —
                  only ever renders a TRUE claim, and only against a fresh
                  verified price. Suppressed when the richer module is shown. */}
              {!showPriceIntel && priceContextBadge && !dealVerdictSupersedesBadge && (
                <div
                  className="mb-3 inline-flex items-center gap-1.5 text-xs font-semibold text-green-800 bg-green-50 border border-green-200 rounded-full px-3 py-1"
                  data-testid="price-context-badge"
                >
                  <span aria-hidden="true">↓</span> {priceContextBadge}
                </div>
              )}

              {/* P05: flag-gated full-history deal verdict — renders nothing
                  when the verdict is null (Real-or-Hidden Rule 3). Suppressed
                  when the richer Price-intelligence module is shown. */}
              {!showPriceIntel && showDealVerdict && (
                <DealVerdict variant="pdp" verdict={dealVerdictData} />
              )}

              <div className="space-y-3">
                {retailers.length === 0 ? (
                  <div className="p-4 rounded-lg bg-white/60 text-center">
                    <p className="text-gray-600 text-sm">
                      This product is currently unavailable from our partner retailers.
                    </p>
                  </div>
                ) : retailers.slice(0, showAllOffers ? retailers.length : 3).map((retailer, index) => {
                  // FRIDGE-122: `pos` says which module a click came from, so
                  // it has to name the row the buy box actually points at.
                  // `index === 0` was the same thing only while the buy box
                  // was always the cheapest row; once selection can move, list
                  // position stops being a stand-in for it and the click would
                  // file itself under 'offer-list'.
                  const isBuyBoxRow = !!retailer.id && retailer.id === bestFreshOffer?.id
                  return (
                  <div key={index} className="rounded-lg bg-[#ff9951]/[0.06] p-3.5">
                    {/* OffersV2: Deal Badges & Countdown */}
                    {(retailer.dealBadge || retailer.dealEndsAt) && (
                      <div className="flex flex-wrap items-center gap-1.5 mb-2">
                        <DealBadge
                          dealBadge={retailer.dealBadge}
                          dealType={retailer.dealType}
                          dealAccessType={retailer.dealAccessType}
                        />
                        {retailer.dealEndsAt && (
                          <DealCountdown dealEndsAt={retailer.dealEndsAt} />
                        )}
                      </div>
                    )}

                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <p className="font-bold text-gray-900 text-base">{retailer.name}</p>
                          {/* P1: never award the badge on a stale/unknown price. */}
                          {retailer.isBuyBoxWinner && !retailer.isPriceUnknown && <BuyBoxWinnerBadge isBuyBoxWinner={true} />}
                        </div>
                        <div className="flex items-baseline gap-2 mt-0.5">
                          {!retailer.isPriceUnknown ? (
                            <>
                              <span className="text-base font-black text-gray-900">{retailer.priceDisplay}</span>
                              {typeof retailer.originalCents === 'number' && typeof retailer.priceCents === 'number' && retailer.originalCents > retailer.priceCents && (
                                <span className="text-xs text-gray-600 line-through">{retailer.originalDisplay}</span>
                              )}
                            </>
                          ) : retailer.id ? (
                            <SignedOfferLink
                              offerId={retailer.id}
                              productId={productData.product.id}
                              pos={isBuyBoxRow ? 'buybox' : 'offer-list'}
                              target="_blank"
                              aria-label={`Check updated pricing at ${retailer.name} (opens retailer site in new tab)`}
                              className="text-xs font-semibold text-[#0469f0] hover:underline"
                            >
                              Check updated pricing →
                            </SignedOfferLink>
                          ) : null}
                        </div>
                        {retailer.merchantName && retailer.merchantName !== retailer.name && (
                          <div className="text-[10px] text-gray-500 mt-0.5">by {retailer.merchantName}</div>
                        )}
                      </div>

                      {retailer.id && (
                        <RetailerOfferCta
                          offerId={retailer.id}
                          productId={productData.product.id}
                          pos={isBuyBoxRow ? 'buybox' : 'offer-list'}
                          target="_blank"
                          aria-label={retailer.isPriceUnknown ? `Check price at ${retailer.name} (opens retailer site in new tab)` : `View offer at ${retailer.name}, ${retailer.priceDisplay} (opens retailer site in new tab)`}
                          hasActiveRecall={hasActiveRecall}
                        >
                          {retailer.isPriceUnknown ? 'Check' : 'View'}
                        </RetailerOfferCta>
                      )}
                    </div>

                    {/* Additional price details */}
                    {!retailer.isPriceUnknown && (retailer.savingsPercentage || retailer.pricePerUnit) && (
                      <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px]">
                        {retailer.savingsPercentage && (
                          <SavingsPercentageBadge
                            savingsPercentage={retailer.savingsPercentage}
                            savingsBasisLabel={retailer.savingsBasisLabel}
                          />
                        )}
                        {retailer.pricePerUnit && (
                          <PricePerUnit pricePerUnit={retailer.pricePerUnit} />
                        )}
                      </div>
                    )}

                    {retailer.availabilityMaxQty && (
                      <div className="mt-1">
                        <LowStockWarning availabilityMaxQty={retailer.availabilityMaxQty} />
                      </div>
                    )}
                  </div>
                  )
                })}
                {/* P1: secondary offers collapsed by default — one dominant
                    CTA beats a wall of choices (buy-box pattern). */}
                {retailers.length > 3 && (
                  <button
                    type="button"
                    onClick={() => setShowAllOffers(!showAllOffers)}
                    className="w-full text-center text-xs font-semibold text-[#0469f0] hover:underline py-1.5"
                    data-testid="offers-collapse-toggle"
                  >
                    {showAllOffers
                      ? 'Show fewer offers ▴'
                      : `Compare ${retailers.length - 3} more offer${retailers.length - 3 === 1 ? '' : 's'} ▾`}
                  </button>
                )}

                {/* FRIDGE-803 (09/04/2026): the second price. Two equal
                    prices in this ladder are not equal when one includes
                    delivery and haul-away — the comparison page says what
                    each retailer publishes, quoted from its own policy. */}
                {retailers.length > 0 && (
                  <Link
                    href="/retailer-policies"
                    className="block text-center text-[11px] font-semibold text-[#0469f0] hover:underline pt-1"
                    data-testid="retailer-policies-link"
                  >
                    Delivery, haul-away and returns at each retailer &rarr;
                  </Link>
                )}

                {/* FRIDGE-664 (owner circle, 08/31/2026): the FRIDGE-117
                    single-retailer notice is REMOVED from the offers box —
                    "remove" drawn over the sentence itself. The coverage
                    state machine lives on in lib/offer-comparison-state.ts
                    for FRIDGE-117's program; nothing renders it here. */}
              </div>

              {/* FTC disclosure adjacent to the affiliate CTA rail. The /r/
                  offer links above are affiliate links; FTC 16 CFR 255 wants a
                  clear, conspicuous disclosure proximate to them — the footer
                  disclaimer and the trust strip sit far below the fold. Reuses
                  the existing approved copy verbatim (P06 FTC audit,
                  SECURITY.md). */}
              {retailers.length > 0 && (
                <div className="mt-3 pt-3 border-t border-[#ff9951]/20">
                  {/* FRIDGE-664 (owner circle, 08/31/2026): the TrustChips row
                      ("Independent · We never sell · N retailers tracked") is
                      REMOVED from the offers box. /search keeps its chips —
                      the circle was drawn on this surface only. */}
                  <AffiliateDisclaimer />
                </div>
              )}

              {/* WS4.2 buy-box filter attach (2026-07-08 reorder) — the
                  highest-leverage attach placement: a one-line nudge in the
                  money zone to the repeat-purchase consumable, anchoring down
                  to the "Complete the Setup" slot (no /r/ link here; that
                  slot's cards carry the monetized CTAs). COPY IS
                  OWNER-APPROVAL-GATED — string below is a placeholder for
                  visual review. Self-hides when no compatible filter. */}
              {(() => {
                const filters = (productData.compatibleFilters || []) as any[]
                if (filters.length === 0) return null
                const f = filters.find((x) => x?.is_oem_match) || filters[0]
                const name = f?.model || f?.title
                if (!name) return null
                const priceStr =
                  typeof f?.lowest_price_cents === 'number' && f.lowest_price_cents > 0
                    ? ` — ${formatCents(f.lowest_price_cents)}`
                    : ''
                return (
                  <button
                    type="button"
                    onClick={() =>
                      document.getElementById('complete-the-setup')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                    }
                    className="mt-3 pt-3 border-t border-[#ff9951]/20 w-full text-left text-xs text-gray-600 hover:text-[#0469f0] transition-colors"
                    data-testid="buybox-filter-attach"
                  >
                    Works with filter <span className="font-semibold">{name}</span>{priceStr} · See it below ↓
                  </button>
                )
              })()}

              {/* FRIDGE-302/577 — the per-page provenance statement, computed
                  from this page's own offer rows and tracked-price stats.
                  Absolute date by design (FRIDGE-78 removed the relative
                  freshness ticker and it stays removed). */}
              {(() => {
                const fresh = (retailers as any[]).filter((r: any) => !r.isPriceUnknown)
                return <ProvenanceLine freshOfferCount={fresh.length} brand={product.brand} />
              })()}

              {/* The single on-page capture watches eligible non-Amazon prices
                  (§6(y)) or partner availability (type back_in_stock). The
                  compatibility flag does not prove global stock absence.
                  Amazon-only loaded offers and the client-fetch fallback
                  remain hidden. The server computes mode eligibility and the
                  API revalidates it. */}
              {SHOW_MODULE_PRICE_WATCH && (
                <PriceWatchForm
                  productId={productData.product.id}
                  productTitle={product.name}
                  hasNonAmazonOffer={(productData.product as any)?.hasNonAmazonOffer === true}
                  isOutOfStock={(productData.product as any)?.isOutOfStock === true}
                  bestNonAmazonPriceCents={(productData.product as any)?.bestNonAmazonPriceCents ?? null}
                />
              )}
            </div>

            {/* FRIDGE-792 — sale frequency: the price-history moat made
                visible per FIN, one honest line under the offers card. Built
                on the same series and density floor as the deal guard
                (FRIDGE-761); null renders nothing. Every figure is a real
                reading — the span is printed as tracked, never rounded to 60. */}
            {saleFrequency && (
              <p data-testid="sale-frequency" className="-mt-3 mb-5 text-xs leading-relaxed text-gray-600">
                {formatSaleFrequencyLine(saleFrequency)}
              </p>
            )}

            {/* Owner direction 08/30: "Why trust Fridge.com" and the
                Price-intelligence module go JUST ABOVE the recall-alert
                opt-in — out of the offers card entirely, so Current Offers
                leads with the offers themselves and the trust/history context
                reads as one block ahead of the alerts ask. */}
            {/* FRIDGE-602: verified trust signals at the buying decision
                point — the one registry, three strongest numbers. */}
            <TrustStrip className="mt-4 rounded-lg border border-gray-100 bg-gray-50/70 px-3 py-2" />

            {/* Lane: the Price-intelligence module is the single price-history
                surface when it renders. It supersedes the standalone 90-day
                badge and the DealVerdict pill (all three would restate the
                same claim). */}
            {showPriceIntel && (
              <div className="mt-4">
                <PriceIntelligence
                  currentCents={bestFreshOffer?.priceCents ?? null}
                  stats={priceIntelStats}
                  series={priceSparkline}
                />
              </div>
            )}

            {/* Recall-alert opt-in — RELOCATED here (owner note 07-20), directly
                below the offers card so the money zone leads the column. Renders
                whenever a FIN exists, independent of active recalls: safety-
                neutral copy, never implies a recall exists. Only the user-
                initiated double-opt-in confirm sends (transactional); recall
                dispatches stay gated OFF behind ALERTS_EMAIL_ENABLED +
                CAN_SPAM_ADDRESS in lib/email-sender.ts. */}
            {/* FRIDGE-333: recall alerts key on a FIN, and a part never presents
                one — the opt-in stays off every part page. */}
            {SHOW_MODULE_RECALL_ALERT && !productIsPart && (productData.product as any)?.fin && (
              <div className="mt-4">
                <RecallAlertForm
                  fin={(productData.product as any).fin}
                  productTitle={product.name}
                />
              </div>
            )}

            {/* Long-form display_description (Layer 2/3 from canonical_products) —
                prefers the cleaned + Sonnet-grounded canonical narrative when
                present (typically 2 paragraphs), falls back to the retailer-
                bridged short description. Both gate on Real-or-Hidden Rule 3
                (renders nothing when null). Now BELOW Current Offers (owner
                note 07-08). Per §33: this is the LONG version (full text), the
                meta description picks the first paragraph. */}
            {(() => {
              const longDesc = (productData.product as any).displayDescription
              if (longDesc) {
                const text = String(longDesc)
                const paras = text.split(/\n\s*\n/).filter((p: string) => p.trim().length > 0)
                // Clamp only when there's meaningfully more than one screenful
                // of copy (~400 chars) — a short two-liner needs no toggle.
                const clampable = text.length > 400
                return (
                  <div className="mt-6 mb-6">
                    <div className={`space-y-3 text-sm text-gray-700 leading-relaxed ${clampable && !descExpanded ? 'line-clamp-4' : ''}`}>
                      {paras.map((para: string, i: number) => (
                        <p key={i}>{para.trim()}</p>
                      ))}
                    </div>
                    {clampable && (
                      <button
                        type="button"
                        onClick={() => setDescExpanded((v) => !v)}
                        className="mt-2 text-sm font-semibold text-[#0469f0] hover:underline"
                        data-testid="description-toggle"
                      >
                        {descExpanded ? 'Read less ▴' : 'Read more ▾'}
                      </button>
                    )}
                  </div>
                )
              }
              return productData.product.description ? (
                <p className="text-sm text-gray-600 leading-relaxed mt-6 mb-6 line-clamp-4">
                  {productData.product.description}
                </p>
              ) : null
            })()}

            {/* Share Buttons */}
            <div className="mb-4">
              <ProductShareButtons
                productTitle={product.name}
                productImage={product.images?.[0]}
                price={product.lowest_price_cents ? product.lowest_price_cents / 100 : undefined}
                variant="top"
              />
            </div>

            {/* FRIDGE-608 — the big Google button on every PDP, beside the
                sharing affordances where a convinced reader acts. */}
            <div className="mb-4">
              <GooglePreferredButton />
            </div>

            {/* Quick-spec 2x2 tiles REMOVED (owner note 07-06): the full
                Technical Specs card now sits directly under the gallery at
                this level, so the tiles were a lower-information duplicate. */}
          </div>
        </div>

        {/* Hero-end sentinel: when it scrolls above the viewport top, the
            sticky buy bars (mobile + desktop) appear. One observer, both bars. */}
        <div ref={heroEndRef} aria-hidden="true" className="h-px w-full" />

        {/* Consensus verdict — ONE module, not two (2026-07-08 reorder).
            When cross-source evidence is rich enough, "The Internet's Verdict"
            renders (research-completion module, spec §7) with the grounded spec
            facts folded into its "Fit & facts" list. Otherwise the heuristic
            "Expert Verdict" card is the fallback (prod default until the VERDICT
            flag flips, and the safety net for thin-evidence products). The two
            never render together — they were competing verdict cards before. */}
        {(() => {
          const wtis = (productData.product as any)?.wtis as WtisRow | null
          // Price fact uses the SAME buybox price the offers card + CTA show
          // (bestFreshOffer.priceDisplay), not product.price (offers[0], pre-sort).
          const verdictFacts = buildVerdictFacts(product, analysis, bestFreshOffer?.priceDisplay ?? null)
          const showInternetVerdict = SHOW_MODULE_VERDICT && hasVerdictEvidence(wtis)

          if (showInternetVerdict) {
            // LLM-free highlights: topic summary from the review-topic index +
            // the single most-helpful POSITIVE quote from the same
            // moderation-gated rows the reviews module renders below (owner
            // note 07-08: no negative review is surfaced at the top level; it
            // must be about the PRODUCT, not the store — exclude retailer
            // purchase-experience reviews).
            const vRows: any[] = (productData.product as any)?.customerReviews || []
            const vTopics: any[] = ((productData.product as any)?.reviewTopicIndex || []).slice(0, 3)
            const withBody = vRows.filter(
              (r) =>
                typeof r.body === 'string' &&
                r.body.trim().length >= 80 &&
                typeof r.rating === 'number' &&
                isProductReview(r.body)
            )
            // Prefer a helpful, complete-but-readable quote (~280 chars).
            const IDEAL_LEN = 280
            const quoteScore = (r: any) =>
              (r.helpful_count ?? 0) * 1000 - Math.abs((r.body?.length ?? 0) - IDEAL_LEN)
            const praise =
              withBody.filter((r) => r.rating >= 4).sort((a, b) => quoteScore(b) - quoteScore(a))[0] ?? null
            const highlights =
              vTopics.length || praise
                ? {
                    topics: vTopics.map((t: any) => ({
                      name: t.display_name as string,
                      count: t.match_count as number,
                      avgRating: typeof t.avg_rating === 'number' ? (t.avg_rating as number) : null,
                      ratedCount: (t.rated_count as number) || 0,
                    })),
                    praise: praise ? { body: praise.body as string, rating: praise.rating as number } : null,
                  }
                : null
            return (
              <InternetVerdict
                wtis={wtis}
                modelLabel={`${displayBrandName(productData.product.brand)} ${displayModel || productTypeName}`.trim()}
                highlights={highlights}
                facts={verdictFacts}
                reviewFaqs={((productData.product as any)?.autoFaqs || []).map((f: any) => ({
                  question: f.question,
                  answer: f.answer,
                  mentionCount: Array.isArray(f.source_review_ids) ? f.source_review_ids.length : null,
                }))}
                hasSpecFaqBelow={
                  moduleFlag('SPEC_FAQ_LEGACY', false) ||
                  ((productData?.specFaqs as any)?.length ?? 0) >= SPEC_FAQ_MIN_RENDER
                }
                cta={
                  bestFreshOffer?.id
                    ? {
                        retailer: bestFreshOffer.name,
                        priceDisplay: bestFreshOffer.priceDisplay,
                        // item 17: signed click (HMAC) via SignedOfferLink
                        // (pos=verdict is applied inside the component;
                        // FRIDGE-276 — no string-built /r/ href here).
                        offerId: bestFreshOffer.id,
                        productId: productData.product.id,
                      }
                    : null
                }
              />
            )
          }

          // Fallback: heuristic Expert Verdict card. Real-or-Hidden Rule 3 —
          // self-hides under 2 grounded facts rather than print boilerplate.
          if (verdictFacts.length < 2) return null
          return (
            <div className="mt-8">
              <div className="p-6 bg-[#ff9951]/[0.06] rounded-lg">
                <h3 className="text-base font-bold text-gray-900 mb-3">Fridge.com Expert Verdict</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {`The ${cleanBrandName(product.brand)}${displayModel ? ` ${displayModel}` : ''} `}
                  <span className="text-gray-500">— {formatProductType(product.type).toLowerCase()}.</span>
                </p>
                <ul className="mt-3 space-y-1.5 text-sm text-gray-700 leading-relaxed list-disc pl-5">
                  {verdictFacts.map((fact, i) => (
                    <li key={i}>{fact}</li>
                  ))}
                </ul>
              </div>
            </div>
          )
        })()}

        {/* Compatible Water Filter standalone block MOVED (2026-07-08 reorder):
            it no longer sits here between the verdict and the specs — a $40
            consumable is an OWNER'S purchase, not a decider's, so it belongs
            in the "Complete the Setup" attach zone (Slot A) far below, after
            the decision content. See #complete-the-setup. */}

        {/* Technical Specs — mobile/tablet flow. The desktop instance lives
            in the gallery column above (owner note 07-06); this one keeps the
            mobile order buybox-first. extraSpecs is P2 SPECS-flag-gated in
            page.tsx — null in prod until flipped. */}
        <div className="mt-8 lg:hidden" id="specifications-mobile">
          <TechnicalSpecs
            product={product}
            productData={productData}
            analysis={analysis}
            extraSpecs={(productData.product as any)?.displaySpecs ?? null}
          />
          {/* FRIDGE-280: manuals-extracted diagrams follow the specs in the
              mobile flow too (idSuffix keeps the anchor unique — same
              convention as specifications / specifications-mobile). */}
          <ManualDiagrams
            diagrams={(productData.product as any)?.displayManualDiagrams}
            idSuffix="-mobile"
            className="mt-4"
          />
        </div>

        {/* §32 Quick Comparison — MOVED UP here (2026-07-08 reorder) to sit in
            the proof cluster right below the specs. It answers the last
            pre-purchase question ("did I pick the right one?") and it
            monetizes BOTH outcomes via per-column /r/ CTAs (signed via
            SignedOfferLink, FIX-13b, pos=compare-col-N — the current product
            is column 0; FRIDGE-276)
            — so it's decision content that happens to
            convert, not a rail. Real-or-Hidden Rule 3: self-hides when neither
            compare targets nor most-compared links resolve. Most-compared is
            its no-data fallback, rendered in this same slot. */}
        {(() => {
          // Quick Comparison targets (COMPARE flag only): scored PIP
          // comparables first, legacy internal-links targets as backup.
          const pipTargets = (((productData?.product as any)?.compareTargets || []) as Array<{
            fin: string; slug: string; brand: string | null; manufacturer_model: string | null;
            display_name: string | null; finish: string | null; type: string | null;
            specs_json: any; features: any; image: string | null; lowest_price_cents: number | null;
            product_id?: string | null; offer_id?: string | null;
          }>)
          const legacyTargets = (((productData?.product as any)?.inlineCompareTargets || []) as Array<{
            fin: string; slug: string; brand: string | null; manufacturer_model: string | null;
            display_name: string | null; finish: string | null; type: string | null;
            specs_json: any; features: any; tier: string | null;
          }>).map(t => ({ ...t, image: null as string | null, lowest_price_cents: null as number | null }))
          const targets = SHOW_MODULE_COMPARE
            ? (pipTargets.length >= 2 ? pipTargets.slice(0, 3) : legacyTargets.slice(0, 2))
            : []

          if (targets.length > 0) {
            const thisProd = productData.product as any
            // compareSelfSpecs (server-merged canonical + structured product
            // fields) beats the raw canonical specs_json — see page.tsx WS6.
            const thisSpecs = thisProd.compareSelfSpecs || thisProd.canonicalSpecsJson || thisProd.specs_json || {}
            const thisFeatures = (thisProd.canonicalFeatures && thisProd.canonicalFeatures.length ? thisProd.canonicalFeatures : (thisProd.features || []))
            const cols = [{
              isCurrent: true,
              slug: null as string | null,
              path: null as string | null,
              displayName: thisProd.displayName || product.name,
              brand: thisProd.brand,
              model: thisProd.manufacturerModel || thisProd.model,
              finish: thisProd.finish,
              specs: thisSpecs,
              features: thisFeatures,
              price: (thisProd.lowest_price_cents ?? null) as number | null,
              image: (Array.isArray(thisProd.images) && thisProd.images.length > 0 ? thisProd.images[0] : null) as string | null,
              offerId: null as string | null, // current col CTAs via bestFreshOffer
              productId: null as string | null,
            }, ...targets.map(t => ({
              isCurrent: false,
              slug: t.slug as string | null,
              // The target's home in ITS family (FRIDGE-333): a part compares
              // against parts, and a part links to /parts/, never through the
              // /products/ 301.
              path: productPagePath({ type: t.type, seo_slug: t.slug }),
              displayName: t.display_name || `${displayBrandName(t.brand)} ${t.manufacturer_model || ''}`.trim(),
              brand: t.brand,
              model: t.manufacturer_model,
              finish: t.finish,
              specs: t.specs_json || {},
              features: Array.isArray(t.features) ? t.features : [],
              price: t.lowest_price_cents,
              image: t.image,
              offerId: (t as any).offer_id ?? null,
              productId: (t as any).product_id ?? null,
            }))]
            // Spec rows via the shared WS6 resolver: priority keys first,
            // coverage in >=2 columns required, hidden-key denylist enforced.
            const usedKeys = pickCompareSpecKeys(cols.map(c => c.specs), 8)
            // FRIDGE-429 — the Price ROW only renders when at least one column
            // has one, and a column with no price shows an em dash. Both facts
            // feed `columnShowsPrice` below, which gates the price cell AND
            // picks each column's CTA label, so a column can never advertise a
            // deal above a blank price cell.
            const priceRowRendered = cols.some(c => hasRenderedPrice(c.price))
            // Truncate long spec decimals to ≤2 places at render time (owner
            // spec 07/20): 36.99999996226 → 36.99. Never mutates stored data.
            const fmtVal = (v: any) => (v === true ? 'Yes' : v === false ? 'No' : formatSpecValue(v))
            return (
              <section id="quick-compare" className="mt-8 scroll-mt-28 rounded-xl bg-gray-50 py-8 px-5 md:px-8 overflow-x-auto">
                <header className="mb-5">
                  <p className="text-[#ff9951] font-bold tracking-widest text-[11px] uppercase mb-1.5">Side-by-Side</p>
                  <h2 className="text-xl md:text-2xl font-black tracking-tighter text-gray-900">Quick Comparison</h2>
                  <p className="text-sm text-gray-500 mt-1 leading-relaxed">How this {productData.product.type?.replace(/[-_]/g, ' ') || 'product'} stacks up against close alternatives on Fridge.com.</p>
                </header>
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr>
                      <th className="text-left text-[11px] uppercase tracking-widest text-gray-600 font-bold pb-3 pr-4 w-24">Spec</th>
                      {cols.map((c, i) => (
                        <th key={i} className={`text-left pb-3 px-3 align-bottom${c.isCurrent ? ' rounded-t-lg bg-[#f2f7ff]' : ''}`}>
                          {c.image ? (
                            <NormalizedProductImage
                              src={c.image}
                              alt={c.displayName ? `${c.displayName} | Fridge.com` : ''}
                              boxClass="w-32 h-32 md:w-40 md:h-40 product-image-canvas rounded-lg mb-2"
                            />
                          ) : null}
                          {c.isCurrent ? (
                            <span className="inline-block text-[10px] font-bold uppercase tracking-wide text-[#0469f0] bg-white ring-1 ring-inset ring-[#0469f0]/25 px-2 py-0.5 rounded-full mb-1.5">This product</span>
                          ) : (
                            <span className="inline-block text-[10px] font-medium uppercase tracking-wide text-gray-600 mb-1.5">Alternative</span>
                          )}
                          <div className="text-sm font-black tracking-tight text-gray-900 line-clamp-2">
                            {c.slug ? (
                              <Link href={c.path ?? `/products/${c.slug}`} className="hover:text-[#0469f0]">{c.displayName}</Link>
                            ) : c.displayName}
                          </div>
                          {c.brand ? <div className="text-xs text-[#0469f0] mt-0.5">{displayBrandName(c.brand)}{resolveDisplayModel(c.model, null) ? ` · ${resolveDisplayModel(c.model, null)}` : ''}</div> : null}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {priceRowRendered ? (
                      <tr>
                        <td className="text-[11px] font-semibold uppercase tracking-wide text-gray-600 py-2.5 pr-4">Price</td>
                        {cols.map((c, i) => (
                          <td key={i} className={`text-sm py-2.5 px-3 tabular-nums ${c.isCurrent ? 'font-black text-gray-900 bg-[#f2f7ff]' : 'font-semibold text-gray-900'}`}>
                            {hasRenderedPrice(c.price)
                              ? `${formatCents(c.price)}`
                              : '—'}
                          </td>
                        ))}
                      </tr>
                    ) : null}
                    {usedKeys.map((k) => (
                      <tr key={k}>
                        <td className="text-[11px] font-semibold uppercase tracking-wide text-gray-600 py-2.5 pr-4">{compareRowLabel(k)}</td>
                        {cols.map((c, i) => (
                          <td key={i} className={`text-sm text-gray-800 py-2.5 px-3${c.isCurrent ? ' bg-[#f2f7ff]' : ''}`}>
                            {c.specs?.[k] != null && String(c.specs[k]).length > 0 ? fmtVal(c.specs[k]) : '—'}
                          </td>
                        ))}
                      </tr>
                    ))}
                    {cols.some(c => c.finish) ? (
                      <tr>
                        <td className="text-[11px] font-semibold uppercase tracking-wide text-gray-600 py-2.5 pr-4">Finish</td>
                        {cols.map((c, i) => <td key={i} className={`text-sm text-gray-800 py-2.5 px-3${c.isCurrent ? ' bg-[#f2f7ff]' : ''}`}>{c.finish || '—'}</td>)}
                      </tr>
                    ) : null}
                    <tr>
                      <td className="text-[11px] font-semibold uppercase tracking-wide text-gray-600 py-2.5 pr-4">Top features</td>
                      {cols.map((c, i) => (
                        <td key={i} className={`text-sm text-gray-800 py-2.5 px-3${c.isCurrent ? ' bg-[#f2f7ff]' : ''}`}>
                          {Array.isArray(c.features) && c.features.length > 0
                            ? <ul className="list-disc list-inside space-y-1">{c.features.slice(0, 3).map((f: string, j: number) => <li key={j}>{f}</li>)}</ul>
                            : '—'}
                        </td>
                      ))}
                    </tr>
                    {/* P2: per-column affiliate CTAs — a shopper who picks the
                        ALTERNATIVE still converts through us (the comparison
                        table becomes a conversion net, not just content). */}
                    <tr>
                      <td className="text-[11px] font-semibold uppercase tracking-wide text-gray-600 py-3 pr-4">Where to buy</td>
                      {cols.map((c, i) => {
                        // The price this column actually paints — the em-dash
                        // cell is not a price, and neither is a suppressed row.
                        const columnShowsPrice = priceRowRendered && hasRenderedPrice(c.price)
                        return (
                        <td key={i} className={`py-3 px-3${c.isCurrent ? ' rounded-b-lg bg-[#f2f7ff]' : ''}`}>
                          {c.isCurrent && bestFreshOffer?.id ? (
                            <RetailerOfferCta
                              offerId={bestFreshOffer.id}
                              productId={productData.product.id}
                              pos={`compare-col-${i}`}
                              target="_blank"
                              aria-label={`${dealCtaLabel(columnShowsPrice)}: ${c.displayName} at ${bestFreshOffer.name}, ${bestFreshOffer.priceDisplay} (opens retailer site in new tab)`}
                              hasActiveRecall={hasActiveRecall}
                              className="inline-block"
                            >
                              {dealCtaLabel(columnShowsPrice)}
                            </RetailerOfferCta>
                          ) : !c.isCurrent && c.offerId && c.productId ? (
                            <SignedOfferLink
                              offerId={c.offerId}
                              productId={c.productId}
                              pos={`compare-col-${i}`}
                              target="_blank"
                              aria-label={`${dealCtaLabel(columnShowsPrice)}: ${c.displayName}${hasRenderedPrice(c.price) ? `, ${formatCents(c.price)}` : ''} (opens retailer site in new tab)`}
                              className="inline-block bg-white text-[#ff8533] hover:bg-[#ff9951]/10 px-4 py-1.5 rounded-lg font-bold text-xs shadow-sm transition-all active:scale-95"
                            >
                              {dealCtaLabel(columnShowsPrice)}
                            </SignedOfferLink>
                          ) : c.slug ? (
                            <Link
                              href={c.path ?? `/products/${c.slug}`}
                              // WCAG 2.4.4 (FRIDGE-796): three alternative columns each
                              // read "See details →" to a different URL — identical
                              // names must mean identical purpose. Visible text unchanged.
                              aria-label={`See details for ${c.displayName}`}
                              className="text-xs font-medium text-[#0469f0] hover:underline"
                            >
                              See details →
                            </Link>
                          ) : (
                            <span className="text-gray-600">—</span>
                          )}
                        </td>
                        )
                      })}
                    </tr>
                  </tbody>
                </table>
              </section>
            )
          }

          // Fallback: Most-compared internal-link rail (prod default until the
          // COMPARE flag flips, and whenever no compare targets resolve).
          // Server-supplied by commerce-fin-internal-links-populator, resolved
          // to live PDP slugs in page.tsx (FIN-suffixed slugs, no ASIN).
          const links = (productData?.product?.internalLinks || []) as Array<{
            target_fin: string
            link_type: 'most_compared' | 'category_overlap' | 'co_view'
            score: number
            slug: string
            path?: string | null
            title: string | null
            image: string | null
            brand: string | null
            lowest_price_cents: number | null
          }>
          const mostCompared = links.filter((l) => l.link_type === 'most_compared').slice(0, 3)
          if (mostCompared.length === 0) return null
          return (
            <section id="most-compared-with" className="mt-8 scroll-mt-28 rounded-xl bg-white border border-gray-200 py-8 px-5 md:px-8">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4 mb-6">
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Shoppers Also Compare</p>
                  <h2 className="text-xl md:text-2xl font-bold text-gray-900">Most-compared with</h2>
                  <p className="text-sm text-gray-600 mt-1">Other models shoppers weigh against this one on Fridge.com.</p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {mostCompared.map((m) => (
                  <Link
                    key={m.target_fin}
                    href={m.path ?? `/products/${m.slug}`}
                    className="block rounded-lg border border-gray-200 hover:border-[#0469f0] hover:shadow-md transition p-4"
                  >
                    <div className="flex items-start gap-3">
                      {m.image ? (
                        <div className="flex-shrink-0 w-20 h-20 bg-gray-50 rounded overflow-hidden">
                          <SafeImage
                            src={m.image}
                            alt={m.title ? `${m.title} | Fridge.com` : ''}
                            width={80}
                            height={80}
                            className="w-full h-full object-contain"
                          />
                        </div>
                      ) : null}
                      <div className="flex-1 min-w-0">
                        {m.brand ? (
                          <p className="text-xs uppercase tracking-wide text-[#0469f0] mb-0.5 truncate">{displayBrandName(m.brand)}</p>
                        ) : null}
                        <p className="text-sm font-medium text-gray-900 line-clamp-2">{m.title || m.slug}</p>
                        {m.lowest_price_cents ? (
                          <p className="text-sm text-gray-700 mt-1">
                            {formatCents(m.lowest_price_cents)}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )
        })()}

        {/* Phase 8: Aggregated NEW-only customer reviews across retailers.
            Reads fin_reviews_raw (pre-filtered server-side on
            condition_excluded=false to enforce NEW-only Rule 1). The component
            self-hides when fewer than 3 reviews exist (Real-or-Hidden Rule 3).
            source_url fields are INTERNAL data — used only as <a href> targets,
            never printed as on-page text (handled inside the component).

            Phase 8 step 8: <ReviewTopicFilter> renders ABOVE <CustomerReviews>
            when ≥10 reviews + ≥2 topics ≥2 matches each. Selecting a topic
            constrains the reviews list to those whose id is in
            matched_review_ids[] for that topic — pure client-side subset of
            the in-memory array (no per-click round trip). Filter chip labels
            use display_name from fin_component_dictionary; brand-policy
            forbids retailer SKU/ASIN labels. */}
        {SHOW_MODULE_REVIEWS && (() => {
          const allReviews: any[] = fullReviews ?? ((productData.product as any)?.customerReviews || [])
          const topicIndex: ReviewTopicEntry[] = ((productData.product as any)?.reviewTopicIndex || []) as ReviewTopicEntry[]
          const filterAvailable = shouldRenderReviewTopicFilter(topicIndex, allReviews.length)
          // If the gate failed, nothing to filter — render reviews as-is.
          // Also clear any orphaned selection so a state change in the topic
          // index (e.g., topic dropped below threshold after a routine run)
          // doesn't strand the UI in a dead-filter state.
          const activeSelection = filterAvailable ? selectedReviewTopic : null
          const allowed = selectedReviewIdSet(topicIndex, activeSelection)
          const visibleReviews = allowed
            ? allReviews.filter((r) => r?.id && allowed.has(r.id))
            : allReviews
          return (
            <div className="mt-8 scroll-mt-28" id="customer-reviews">
              {filterAvailable && (
                <ReviewTopicFilter
                  topicIndex={topicIndex}
                  totalReviews={allReviews.length}
                  selectedTopicId={activeSelection}
                  onSelect={(id) => {
                    // Topic filtering slices the corpus — make sure it exists.
                    ensureReviewCorpus()
                    setSelectedReviewTopic(id)
                  }}
                />
              )}
              <CustomerReviews
                reviews={visibleReviews}
                onNeedCorpus={ensureReviewCorpus}
                corpusLoading={reviewCorpusLoading}
                corpusTotal={(productData.product as any)?.reviewsMeta?.eligibleCount ?? allReviews.length}
                // PIP WS2/WS5: histogram + cross-source rollup + moderation
                // state computed server-side by getDisplayReviews.
                meta={(productData.product as any)?.reviewsMeta ?? null}
                // P2: post-reading conversion CTA (buybox winner, fresh price only).
                inlineCta={
                  bestFreshOffer?.id
                    ? {
                        retailer: bestFreshOffer.name,
                        priceDisplay: bestFreshOffer.priceDisplay,
                        // item 17: signed click (HMAC) via SignedOfferLink
                        // (pos=reviews-inline / reviews-lightbox applied inside
                        // the component; FRIDGE-276 — no string-built /r/ href).
                        offerId: bestFreshOffer.id,
                        productId: productData.product.id,
                      }
                    : null
                }
              />
            </div>
          )
        })()}

        {/* Video reviews — Keepa main + additional + customer videos pulled
            into fin_videos by commerce-keepa-incidental-videos. Component
            self-hides when zero rows (Real-or-Hidden Rule 3). source_url is
            INTERNAL (only consumed as a player source) — never printed as
            on-page text per brand policy. */}
        {SHOW_MODULE_VIDEOS && (
          <div className="mt-8 scroll-mt-28" id="video-reviews">
            <VideoReviews
              videos={(productData.product as any)?.videos as FinVideo[] | undefined}
            />
          </div>
        )}

        {/* Review-grounded FAQ (fin_faqs) MOVED UP into "The Internet's Verdict"
            as the "Owners Answer" block (2026-07-09) — it now lives inside the
            verdict module (reviewFaqs prop), clearly attributed as internet-
            sourced and distinct from the spec FAQ. The standalone AutoFAQ render
            was removed to avoid duplication; the FAQPage JSON-LD still unions
            these rows server-side (see ProductSchemas), and AutoFAQ.tsx remains
            for the compare page + schema. The spec-derived FAQ (ProductFAQ) still
            renders below as "Frequently Asked Questions". */}

        {/* Manuals & Guides — manufacturer PDFs (owner's manuals, spec
            sheets, install guides, warranty terms) sourced from
            canonical_products.pdfs jsonb, normalized and mode-gated
            server-side by getDisplayPdfs (strict = held in our storage and
            fronted to a fridge.com/manuals path outside dev; dev preview shows
            discovered ones with an "unverified" badge). Default-ON since 07-06 — it replaces the
            removed Parts & Manuals card. Hosted URLs are rewritten through
            fridge.com via lib/image-url getImagePublicUrl so no raw
            *.supabase.co host leaks (Brand Policy / Rule 4). Component
            self-hides when zero rows (Real-or-Hidden Rule 3). */}
        {SHOW_MODULE_PDFS && (
          <div className="mt-8" id="manuals-guides">
            <ProductDocuments
              docs={(productData.product as any)?.displayPdfs as DisplayPdf[] | undefined}
            />
            {/* Item 52: verbatim quotes from this model's own documents
                (clearances, temperature range, care, warranty). Resolved
                server-side; null unless real content exists — self-hides. */}
            <FromTheManual
              guidance={(productData.product as any)?.displayManualGuidance}
              className="mt-4"
            />
          </div>
        )}

        {/* Trust strip — replaces the "Smart Insights / Why Shop / Save"
            50/25/25 marketing row (conversion spec P0). Every claim here is
            backed by data on THIS page: freshness from fin_freshness, the
            analyzed-review count from the harvested corpus, and the
            FTC-clean affiliate disclosure. The old row carried a fabricated
            "50+ retailers" claim (content-quality audit) and generic filler
            that pushed conversion content below the fold. */}
        {(() => {
          const lastmod: string | null = (productData.product as any)?.finFreshnessLastmod ?? null
          // Item 4 (owner 07/20): the "N owner reviews analyzed" count must come
          // from the SAME source the Internet's Verdict headline counts (fin_wtis
          // .review_count), so the two numbers can never disagree on one page.
          // Fall back to the reviews-rollup rating count only when the verdict
          // row is absent (thin-evidence products with no WTIS pass yet).
          const trustWtis = (productData.product as any)?.wtis as WtisRow | null
          const wtisReviewCount: number =
            typeof trustWtis?.review_count === 'number' ? trustWtis.review_count : 0
          const totalRatings: number =
            wtisReviewCount > 0
              ? wtisReviewCount
              : ((productData.product as any)?.reviewsMeta?.totalRatings ?? 0)
          const dateLabel = lastmod
            ? new Date(lastmod).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
            : null
          return (
            <div
              className="mt-8 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 flex flex-wrap items-center justify-center gap-x-6 gap-y-1 text-xs text-gray-600"
              data-testid="trust-strip"
            >
              {dateLabel && <span>Prices &amp; data checked {dateLabel}</span>}
              {totalRatings > 0 && <span>{totalRatings.toLocaleString()} owner reviews analyzed</span>}
              <span>
                We may earn a commission when you buy through our links — it never affects our rankings.
              </span>
            </div>
          )
        })()}

        {/* Cool Right Now / Most-compared / Quick Comparison MOVED (2026-07-08
            reorder). The trending "Cool Right Now" rail was CUT from PDPs
            (trending framing belongs on discovery pages). Quick Comparison
            moved UP to right below the specs — it is decision content that
            monetizes both outcomes (per-column CTAs), so it belongs with the
            proof modules, not buried below the rails. Most-compared is now its
            no-data fallback in that same slot. See #quick-compare above. */}

        {/* ═══ FEATURED SLOT A — "Complete the Setup" (2026-07-08 reorder) ═══
            The attach zone, placed right after the trust strip: this is the
            ONE spot where showing more products can't cannibalize the primary
            sale, because attach items COMPLETE the purchase rather than
            compete with it. Two halves merged into one section:
              1. Compatible water filters (the repeat-purchase consumable —
                 renders in prod regardless of the COMPARE dev flag, since
                 filters are already live).
              2. Frequently-bought-together (Keepa related_asins → live
                 affiliate offers, pos=fbt) — still COMPARE-gated.
            Self-hides when neither half has data (Real-or-Hidden Rule 3). */}
        {(() => {
          const filters = (productData.compatibleFilters || []) as any[]
          const fbt = (((productData?.product as any)?.fbtTargets || []) as Array<{
            fin: string; slug: string; path?: string | null; title: string | null; image: string | null;
            lowest_price_cents: number | null; product_id: string; offer_id: string | null;
          }>)
          const showFilters = filters.length > 0
          const showFbt = SHOW_MODULE_COMPARE && fbt.length > 0
          if (!showFilters && !showFbt) return null
          const typeLabel = productData.product.type?.replace(/[-_]/g, ' ') || 'product'
          return (
            <section id="complete-the-setup" className="mt-10 rounded-xl bg-white border border-gray-200 py-8 px-5 md:px-8" data-testid="fbt-section">
              <div className="mb-6">
                <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Complete the Setup</p>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900">Everything for this {typeLabel}</h2>
                <p className="text-sm text-gray-600 mt-1">Owners of this {typeLabel} usually buy these with it.</p>
              </div>
              {showFilters && (
                <div className="mb-6">
                  <CompatibleFilterSection
                    filters={filters}
                    fridgeBrand={displayBrandName(product.brand)}
                    totalFilters={Number((productData as { compatibleFiltersTotal?: number }).compatibleFiltersTotal ?? filters.length)}
                  />
                </div>
              )}
              {showFbt && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {fbt.map((item) => {
                    // FRIDGE-429 — ONE boolean gates the price AND the label.
                    const showsPrice = hasRenderedPrice(item.lowest_price_cents)
                    return (
                    <div key={item.fin} className="rounded-lg border border-gray-200 p-4 flex flex-col">
                      <Link href={item.path ?? `/products/${item.slug}`} className="block flex-1">
                        {item.image ? (
                          <div className="w-full aspect-square product-image-canvas rounded mb-3 overflow-hidden">
                            <SafeImage src={item.image} alt={item.title ? `${item.title} | Fridge.com` : ''} width={160} height={160} className="w-full h-full object-contain" />
                          </div>
                        ) : (
                          <div className="w-full aspect-square product-image-canvas rounded mb-3" aria-hidden="true" />
                        )}
                        <p className="text-sm font-semibold text-gray-900 line-clamp-2 leading-snug hover:text-[#0469f0]">{item.title || item.slug}</p>
                      </Link>
                      <div className="mt-3 flex items-center justify-between gap-2">
                        {showsPrice ? (
                          <span className="text-sm font-black text-gray-900">
                            {formatCents(item.lowest_price_cents)}
                          </span>
                        ) : <span />}
                        {item.offer_id && (
                          <SignedOfferLink
                            offerId={item.offer_id}
                            productId={item.product_id}
                            pos="fbt"
                            target="_blank"
                            aria-label={`${dealCtaLabel(showsPrice)}: ${item.title || item.slug}${showsPrice ? `, ${formatCents(item.lowest_price_cents)}` : ''} (opens retailer site in new tab)`}
                            className="bg-[#ff9951] hover:bg-[#ff8533] text-[var(--color-brand-action-text)] px-4 py-1.5 rounded-lg font-bold text-xs transition-all shadow-sm active:scale-95"
                          >
                            {dealCtaLabel(showsPrice)}
                          </SignedOfferLink>
                        )}
                      </div>
                    </div>
                    )
                  })}
                </div>
              )}
            </section>
          )
        })()}

        {/* FRIDGE-731 — what this filter listing claims (NSF standards, lead/
            PFAS, capacity, service life), computed on the server from the
            listing's own text; null when it claims nothing. */}
        {productData.filterCertification && <FilterCertificationTable evidence={productData.filterCertification} />}
        {productData.filterPerformance?.status === 'manufacturer-sheet' && <FilterPerformanceTable sheet={productData.filterPerformance.sheet} />}
        {productData.filterClaims && <ListingClaimsCard claims={productData.filterClaims} />}

        {/* ═══ Filter-side PDP sections (W24) ═══
            On FILTER product pages only: (a) the refrigerators this filter
            fits (reverse compat, OEM-verified first) and (b) other filters in
            the same OEM family. Purely owned data; each half self-hides when
            empty (Real-or-Hidden Rule 3). */}
        {(() => {
          const fits = (productData.fitsFridges || []) as Array<{
            id: string; title: string; brand: string | null; model: string | null;
            seo_slug: string | null; images: string[] | null;
            lowest_price_cents: number | null; is_oem_match: boolean;
          }>
          const family = (productData.familyFilters || []) as Array<{
            id: string; title: string; brand: string | null; model: string | null;
            seo_slug: string | null; path?: string | null; images: string[] | null; lowest_price_cents: number | null;
          }>
          const familyName = (productData.oemFilterFamily || '') as string
          // FRIDGE-853: the grid renders every row the server sent (the fetch
          // is capped at FITS_FRIDGE_CAP), so `fits.length` IS what is visible
          // — which is also what the JSON-LD asserts. Schema equals visible.
          const fitsCoverage = describeFitmentCoverage(
            fits.length,
            Number((productData as { fitsFridgesTotal?: number }).fitsFridgesTotal ?? fits.length),
            { one: 'refrigerator', many: 'refrigerators' },
          )
          if (fits.length === 0 && family.length === 0) return null
          return (
            <section id="filter-compatibility" className="mt-10 rounded-xl bg-white border border-gray-200 py-8 px-5 md:px-8" data-testid="filter-fits-section">
              {fits.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Compatibility</p>
                  <h2 className="text-xl md:text-2xl font-bold text-gray-900">Fits these refrigerators</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Verified from this filter&apos;s own listed fit data — always confirm your model number before ordering.
                  </p>
                  {/* FRIDGE-853 — the honest coverage line. 103 of the 177
                      filter pages that carry fit data fit MORE refrigerators
                      than this grid holds, and the page used to end at the
                      twelfth card with nothing said, which reads as "twelve is
                      all there is". The total counts only rows that could
                      render, and the sentence is suppressed entirely when the
                      grid is showing everything. */}
                  {fitsCoverage && (
                    <p className="text-sm font-semibold text-gray-700 mt-2">{fitsCoverage}</p>
                  )}
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mt-5">
                    {fits.map((f) => (
                      <Link key={f.id} href={`/products/${f.seo_slug || f.id}`} className="rounded-lg border border-gray-200 p-4 hover:border-[#ff9951]/60 hover:shadow-md transition-all block">
                        {f.images?.[0] && (
                          <div className="w-full aspect-square product-image-canvas rounded mb-3 overflow-hidden">
                            <SafeImage src={f.images[0]} alt={f.title ? `${f.title} | Fridge.com` : ''} width={140} height={140} className="w-full h-full object-contain" />
                          </div>
                        )}
                        <p className="text-sm font-semibold text-gray-900 line-clamp-2 leading-snug">{f.title}</p>
                        <div className="mt-2 flex items-center gap-2">
                          {f.model && <span className="text-xs text-gray-500 truncate">{f.model}</span>}
                          {f.is_oem_match && (
                            <span className="text-[10px] font-bold uppercase tracking-wide text-[#0d8449] bg-[#ecfdf5] px-1.5 py-0.5 rounded">OEM</span>
                          )}
                        </div>
                        {/* FRIDGE-397 — the owner's ask: this block should read like a
                            product surface, with a real price. Everything else it needed
                            was already here (mirrored image, real FIN-slugged row, link
                            to the appliance's own page); the price was the one gap.

                            The data was already selected and already typed — page.tsx
                            pulls `lowest_price_cents` on `fridge_product` and the local
                            type declares it — so this is a render omission, not missing
                            data. Same guard and same markup as the "Other compatible
                            filters" block twenty lines below, deliberately: one treatment
                            for price on this page, not two.

                            Real-or-Hidden: no price renders when there is none. A
                            compatible fridge with no live offer shows its image, title
                            and model and simply carries no figure — never a placeholder,
                            a zero, or "call for price". */}
                        {typeof f.lowest_price_cents === 'number' && f.lowest_price_cents > 0 && (
                          <p className="text-sm font-black text-gray-900 mt-2">
                            {formatCents(f.lowest_price_cents)}
                          </p>
                        )}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
              {family.length > 0 && (
                <div className={fits.length > 0 ? 'mt-8' : ''}>
                  <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Same OEM family</p>
                  <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-4">
                    Other {familyName ? `${familyName.toUpperCase()} ` : ''}compatible filters
                  </h2>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {family.slice(0, 8).map((f) => (
                      <Link key={f.id} href={f.path ?? `/products/${f.seo_slug || f.id}`} className="rounded-lg border border-gray-200 p-4 hover:border-[#ff9951]/60 hover:shadow-md transition-all block">
                        {f.images?.[0] && (
                          <div className="w-full aspect-square product-image-canvas rounded mb-3 overflow-hidden">
                            <SafeImage src={f.images[0]} alt={f.title ? `${f.title} | Fridge.com` : ''} width={120} height={120} className="w-full h-full object-contain" />
                          </div>
                        )}
                        <p className="text-sm font-semibold text-gray-900 line-clamp-2 leading-snug">{f.title}</p>
                        {typeof f.lowest_price_cents === 'number' && f.lowest_price_cents > 0 && (
                          <p className="text-sm font-black text-gray-900 mt-2">
                            {formatCents(f.lowest_price_cents)}
                          </p>
                        )}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </section>
          )
        })()}

        {/* FRIDGE-731 — the filter tools from the filter product page: the model
            hub that lists this filter, the finder's number lookup and the
            model-number locator. Computed on the server; null off filter pages. */}
        {productData.filterToolLinks && <FilterPdpToolLinks links={productData.filterToolLinks} />}

        {/* ═══ FEATURED SLOT B — "Closest alternatives" (2026-07-08 reorder) ═══
            The escape hatch, placed AFTER the decision content: a shopper who
            got this far without clicking out is drifting from THIS product, so
            scoped alternatives (same type, ±20% price, form-factor-matched,
            de-duped against the comparison slot) keep the commission on our
            site instead of losing them to a Google search. Framed as advice
            ("Still deciding?"), not as an ad. Min 4 items or it self-hides.
            Card clicks expose their slot to PostHog autocapture.

            De-dupe is by SLUG against whatever the compare slot ACTUALLY
            renders — the fetch already excludes PIP compareTargets by
            product_id, but in the prod default (COMPARE flag off) the slot
            shows the most-compared internal-link fallback, whose rows carry no
            product_id. Building the exclusion from every compare-slot source's
            slug keeps "never the same alt twice" true on all three paths
            (PIP table / legacy table / most-compared fallback). */}
        {(() => {
          const p = productData.product as any
          const comparedSlugs = new Set<string>(
            [
              ...((p?.compareTargets || []) as Array<{ slug?: string }>),
              ...((p?.inlineCompareTargets || []) as Array<{ slug?: string }>),
              ...(((p?.internalLinks || []) as Array<{ slug?: string; link_type?: string }>).filter(
                (l) => l.link_type === 'most_compared'
              )),
            ]
              .map((t) => t.slug)
              .filter((s): s is string => typeof s === 'string' && s.length > 0)
          )
          const slugOf = (u: string) => (u || '').replace(/^\/products\//, '').replace(/^\/+/, '')
          const rail = comparedSlugs.size
            ? closestAltProducts.filter((prod) => !comparedSlugs.has(slugOf(prod.canonical_url)))
            : closestAltProducts
          return (
            <FullBleedProductCarousel
              products={rail}
              label="Still Deciding?"
              title={`Closest alternatives to this ${collectionDisplayPlural.replace(/s$/, '').toLowerCase()}`}
              subtitle="Same size and budget — the models shoppers cross-shop with this one."
              slot="rail-b"
              minItems={4}
              maxItems={8}
            />
          )
        })()}

        {/* §31 Similar Products Grid — MOVED above Brand Story (2026-07-08
            reorder): same-brand alternatives are shopping content and belong
            with the featured rails, while the brand narrative is SEO/trust
            filler that reads lower. Same brand + same type, ranked by
            data_depth_score. Real-or-Hidden Rule 3: hides when fewer than 3
            results survive the seo_slug + pdp_eligible gate. */}
        {(() => {
          const sims: any[] = ((productData?.product as any)?.similarProducts || []).filter((s: any) => s?.slug)
          if (sims.length < 3) return null
          const productType = (productData.product.type || 'product').replace(/[-_]/g, ' ')
          return (
            <section id="similar-products" className="mt-10 rounded-xl bg-white border border-gray-200 py-8 px-5 md:px-8">
              <div className="mb-6">
                <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">More from {displayBrandName(productData.product.brand)}</p>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900">Similar {productType}s from {displayBrandName(productData.product.brand)}</h2>
                <p className="text-sm text-gray-600 mt-1">Other models in the same family — useful for comparing capacity, finish, and feature mix.</p>
              </div>
              {/* FRIDGE-662 (owner: "missing brand style product cards"):
                  the rail's hand-rolled image+title+price tiles are replaced
                  with THE brand-kit ProductGridCard — the same card the
                  homepage and collection grids render — so the rail matches
                  the rest of the site (image canvas, brand eyebrow, price via
                  the one sitewide formatter, the FRIDGE-398 no-image tile).
                  Price style note, recorded because the owner's screenshot
                  flagged "$2,399 beside $2,349.99": that IS the sitewide
                  formatPrice rule (whole dollars drop .00) — the brand cards
                  render it identically, so the rail is now consistent BY
                  sharing the formatter, not by forcing cents. */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                {sims.map((s) => (
                  <ProductGridCard
                    key={s.fin}
                    images={s.image ? [s.image] : []}
                    brand={displayBrandName(productData.product.brand)}
                    title={s.title || s.slug}
                    price={s.lowest_price_cents ? formatCents(s.lowest_price_cents) : undefined}
                    href={s.path ?? `/products/${s.slug}`}
                    alt={s.title ? `${s.title} | Fridge.com` : undefined}
                    fin={s.fin ?? null}
                  />
                ))}
              </div>
            </section>
          )
        })()}

        {/* §31 Brand Story Panel — narrative + parent + founding.
            Renders only when the brand row has at least narrative_text OR
            (parent_company AND founding_year). Real-or-Hidden Rule 3. */}
        {(() => {
          const bs: any = (productData?.product as any)?.brandStory
          if (!bs) return null
          // Matches the rule above: a real story, or at least parentage +
          // founding to make the facts column worth a section. A lone
          // headquarters/founding row is not a brand story.
          const hasContent = !!(bs.narrative || (bs.parentCompany && bs.foundingYear))
          if (!hasContent) return null
          return (
            <section id="brand-story" className="mt-10 rounded-xl bg-white border border-gray-200 py-8 px-5 md:px-8">
              <div className="mb-5">
                <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Brand Background</p>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900">About {displayBrandName(bs.name)}</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-2 space-y-3 text-sm text-gray-700 leading-relaxed">
                  <div className="flex min-h-16 items-center justify-start">
                    <BrandTileContent
                      key={`${bs.name}:${bs.logoPath || ''}`}
                      tile={{ name: displayBrandName(bs.name), slug: bs.slug, logo: bs.logoPath }}
                      logoClass="h-16 w-48 max-w-full object-contain object-left"
                      logoHeight={64}
                      logoWidth={192}
                      compact={false}
                    />
                  </div>
                  {bs.narrative ? String(bs.narrative).split(/\n\s*\n/).filter((p: string) => p.trim()).map((para: string, i: number) => (
                    <p key={i}>{para.trim()}</p>
                  )) : null}
                  {/* Manufacturer-site link removed (conversion spec P0):
                      non-affiliate exit. Brands with affiliate programs
                      appear as buybox offers instead. */}
                </div>
                <dl className="space-y-2.5 text-sm border-l border-gray-100 pl-5">
                  {bs.foundingYear ? (
                    <div><dt className="text-xs uppercase tracking-wide text-gray-500">Founded</dt><dd className="font-medium text-gray-900">{bs.foundingYear}</dd></div>
                  ) : null}
                  {/* FRIDGE-224 — Headquarters row removed on the owner's
                      direction (08/01/2026). The `hasContent` gate above never
                      referenced headquarters, so the panel's suppress
                      condition is unchanged. The DB column and the brand-page
                      surfaces are deliberately untouched. */}
                  {bs.parentCompany ? (
                    <div><dt className="text-xs uppercase tracking-wide text-gray-500">Parent company</dt><dd className="font-medium text-gray-900">{bs.parentCompany}</dd></div>
                  ) : null}
                  {bs.patentCount ? (
                    <div><dt className="text-xs uppercase tracking-wide text-gray-500">Patents on file</dt><dd className="font-medium text-gray-900">{bs.patentCount.toLocaleString()}</dd></div>
                  ) : null}
                  {typeof bs.activeRecallCount === 'number' && bs.activeRecallCount > 0 ? (
                    <div><dt className="text-xs uppercase tracking-wide text-gray-500">Active CPSC recalls</dt><dd className="font-medium text-amber-700">{bs.activeRecallCount}</dd></div>
                  ) : null}
                  {bs.brandRatingAvg && bs.brandRatingCount ? (
                    <div><dt className="text-xs uppercase tracking-wide text-gray-500">Brand rating (aggregate)</dt><dd className="font-medium text-black">{Number(bs.brandRatingAvg).toFixed(1)}/5 · {Number(bs.brandRatingCount).toLocaleString()} reviews</dd></div>
                  ) : null}
                </dl>
              </div>
            </section>
          )
        })()}

        {/* ═══ FEATURED SLOT C — "Top-rated {category}" (2026-07-08 reorder) ═══
            The last big net, after the brand story. Top-rated framing is the
            right final pitch for the trust-seekers who scroll this deep.
            Reuses getFeaturedProductsWithFallback (topRatedProducts).
            Card clicks expose their slot to PostHog autocapture.
            Min 4 items or it self-hides.
            The old "Best Value" and site-wide "Bestsellers" rails were CUT —
            Slot B's price-band scoping absorbs value, and a cross-category
            bestsellers mix only diluted a PDP. */}
        <FullBleedProductCarousel
          products={topRatedProducts}
          label="Highest Rated"
          title={`Top-Rated ${collectionDisplayPlural}`}
          subtitle={`The highest-reviewed ${productNoun}s by verified owners and experts.`}
          slot="rail-c"
          minItems={4}
          maxItems={12}
        />

        <div className="mt-2.5 sm:mt-3 space-y-3 px-2 sm:px-0">
          {/* FAQ — MOVED above Related Resources (2026-07-08 reorder): keep
              the SEO tail (resources, brand grid) at the very bottom.
              FAQ REWRITE (docs/Page-Types-And-Templates.md): the default
              is the data-gated SpecFAQ built server-side in page.tsx
              (initialData.specFaqs — schema and visible content share that one
              source). The legacy template-based ProductFAQ renders ONLY in recovery
              mode (NEXT_PUBLIC_MODULE_SPEC_FAQ_LEGACY='1'); its old JSON-LD
              block is retired permanently either way. */}
          {moduleFlag('SPEC_FAQ_LEGACY', false) ? (
            <ProductFAQ product={product} analysis={analysis} />
          ) : (
            <SpecFAQ
              faqs={(productData?.specFaqs as any) || []}
              offerCount={Array.isArray(retailers) ? retailers.length : 0}
            />
          )}
        </div>

        {/* §33 Product Identifiers REMOVED (conversion spec P0): it surfaced
            non-partner retailer SKUs and told shoppers to "verify inventory
            with non-partner retailers" — an instruction to shop elsewhere.
            FIN + model remain in the header; retailer SKUs stay internal. */}

        <div className="mt-2.5 sm:mt-3 space-y-3 px-2 sm:px-0">
          {/* Related Resources — 4×2 grid; all slugs validated server-side */}
          {relatedGuides.length > 0 && (
            <section id="related-resources" className="mt-6">
              <h2 className="text-base font-bold text-gray-900 mb-4">Related Resources</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {relatedGuides.map((guide, slot) => (
                  <ToolLinkCard
                    key={guide.slug}
                    title={guide.title}
                    // FRIDGE-661 (owner: "all subheadders need variety"):
                    // FRIDGE-604's slug-prefix ternary blanketed whole grids
                    // with its hub fallback — false for guides and ranked
                    // lists. hubCardDescription reads the registry-backed
                    // hub_type and alternates phrasing by slot.
                    description={hubCardDescription(guide, slot)}
                    href={`/${guide.slug}`}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Explore Other Brands — same component used on hub pages */}
          {(() => {
            // FRIDGE-1420: each tile links /brands/<page_slug>, the live brand
            // page /api/brands resolved with the one brand-page rule (its own,
            // or the brand page its redirect lands on). A brand without one
            // leaves the grid: 156 of the 267 partner brands answered 308 or
            // 404 on 10/06/2026, Summit among the first ten tiles. The product's
            // own brand page is never offered as "another" brand.
            const brandPeers = (brands || [])
              .filter((b: any) =>
                b?.page_slug &&
                b?.logo_path &&
                !b.logo_path.includes('placeholder') &&
                // FRIDGE-1626: by canonical brand, so "LG Electronics" is LG on an LG page.
                (!canonicalBrandKey(product.brand || '') || canonicalBrandKey(b.name || '') !== canonicalBrandKey(product.brand || '')) &&
                b.page_slug !== product.brandPageSlug
              )
              .map((b: any) => ({ name: b.name, slug: b.page_slug, logo: getProxiedStoragePath(b.logo_path) }))
            return brandPeers.length > 0 ? (
              <BrandLogoGrid
                brands={brandPeers}
                title="Explore Other Brands"
                description="Compare top refrigerator and freezer manufacturers on Fridge.com."
                initialCount={10}
                searchable
                searchPlaceholder="Search brands…"
                totalCount={allBrandsLoaded ? undefined : serverBrandsTotal ?? undefined}
                onNeedAll={allBrandsLoaded ? undefined : loadAllBrands}
              />
            ) : null
          })()}
        </div>

        <RecentViewRecorder product={{ id: pdpSavedItem.id, fin: pdpSavedItem.fin, brand: pdpSavedItem.brand, title: pdpSavedItem.title, href: pagePath, image: pdpSavedItem.image }} />
        <RecentReturn excludeId={pdpSavedItem.id} />
      </div>

      {/* Desktop sticky utility bar (2026-07-08 reorder) — the desktop
          counterpart to the mobile bar below: a persistent buy CTA + section
          anchors once the hero scrolls past. Anchors list only the sections
          that actually render this page. pos=sticky-desktop. */}
      {(() => {
        const p = productData.product as any
        const reviewsCount = (p?.customerReviews || []).length
        const faqCount = (p?.autoFaqs || []).length
        const pipCount = (p?.compareTargets || []).length
        const legacyCount = (p?.inlineCompareTargets || []).length
        const mcCount = ((productData?.product?.internalLinks || []) as Array<{ link_type?: string }>)
          .filter((l) => l.link_type === 'most_compared').length
        const qcRenders = SHOW_MODULE_COMPARE && (pipCount >= 2 || legacyCount >= 1)
        const compareId = qcRenders ? 'quick-compare' : mcCount > 0 ? 'most-compared-with' : null
        const anchors: PdpStickyBarAnchor[] = [{ id: 'specifications', label: 'Specs' }]
        if (compareId) anchors.push({ id: compareId, label: 'Compare' })
        if (SHOW_MODULE_REVIEWS && reviewsCount >= 3) anchors.push({ id: 'customer-reviews', label: 'Reviews' })
        if (SHOW_MODULE_FAQ && faqCount >= 3) anchors.push({ id: 'faq', label: 'FAQ' })
        return (
          <PdpStickyBar
            visible={showStickyCta}
            productName={product.name}
            productId={productData.product.id}
            image={product.images?.[0]}
            offer={
              bestFreshOffer?.id
                ? { id: bestFreshOffer.id, name: bestFreshOffer.name, priceDisplay: bestFreshOffer.priceDisplay }
                : null
            }
            anchors={anchors}
            hasActiveRecall={hasActiveRecall}
            saveItem={pdpSavedItem}
          />
        )
      })()}

      {/* P1: mobile sticky buy bar — the single biggest mobile-conversion
          lever. Appears after the buybox scrolls away; mobile-only so it
          never stacks with desktop layouts; z-index below ComparisonBar so
          an active comparison selection wins the slot. Reuses the same /r/
          redirect, signed via SignedOfferLink (FIX-13b) with pos=sticky for
          per-module EPC attribution (desktop bar: pos=sticky-desktop). */}
      {showStickyCta && bestFreshOffer?.id && (
        <MobileStickyBuyBar
          offer={{ id: bestFreshOffer.id, name: bestFreshOffer.name, priceDisplay: bestFreshOffer.priceDisplay }}
          productId={productData.product.id}
          savedItem={pdpSavedItem}
          hasActiveRecall={hasActiveRecall}
        />
      )}

      {/* Sticky Comparison Bar */}
      <ComparisonBar />
    </>
  )
}
